'use client';
import React, { useEffect, useRef, useState } from 'react';
import data from '../../data/portfolios/works-onboarding';
import googleLogo from '../../assets/images/logo/google.png';
import { auth, googleProvider, actionCodeSettings } from '../../config/firebase';
import { signInWithPopup, createUserWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { createProject, associateProjectWithUser, updateProject, getUserProjects, deleteProject } from '../../services/projectService';
import serverConfig from '../../config/server-port.json';
import axios from 'axios';
import fileDownload from 'js-file-download';
import './Generate.css';
import { getSignedUrl } from '../../services/storageService';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { API_URL, getAuthHeaders } from '../../services/storageService';
import { Document, Page, Text, View, StyleSheet, Font, pdf } from '@react-pdf/renderer';
import { trackPageView, trackSignIn, trackSignUp, trackBrandCreated, trackBrandShared, trackBrandDownloaded, trackError } from '../../config/analytics';

// Register system fonts
Font.register({
  family: 'System',
  fonts: [
    {
      src: 'https://fonts.cdnfonts.com/s/59278/PlusJakartaSans-Regular.woff',
      fontWeight: 'normal',
    },
    {
      src: 'https://fonts.cdnfonts.com/s/59278/PlusJakartaSans-Bold.woff',
      fontWeight: 'bold',
    }
  ]
});

// Create styles
const styles = StyleSheet.create({
  page: {
    paddingTop: 65,
    paddingRight: 50,
    paddingBottom: 50,
    paddingLeft: 50,
    fontFamily: 'Helvetica',
    backgroundColor: 'rgb(15, 15, 15)',
    color: 'rgb(255, 255, 255)',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 40, // Increased from 30 to 40 to add more space
    borderBottom: '1 solid rgb(255, 255, 255)',
    paddingBottom: 10,
    position: 'absolute',
    top: 30,
    left: 50,
    right: 50,
  },
  headerLeft: {
    fontSize: 12,
    color: 'rgb(253, 91, 56)',
  },
  headerRight: {
    fontSize: 12,
    color: 'rgb(255, 255, 255)',
    textAlign: 'right',
  },
  section: {
    marginTop: 60,
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
    color: 'rgb(255, 255, 255)',
  },
  sectionContent: {
    fontSize: 12,
    lineHeight: 1.5,
    color: 'rgb(255, 255, 255)',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    textAlign: 'center',
    fontSize: 10,
    color: 'rgb(255, 255, 255)',
    borderTop: '1 solid rgb(255, 255, 255)',
    paddingTop: 10,
  },
  strong: {
    fontWeight: 'bold',
    color: 'rgb(255, 255, 255)',
  },
  em: {
    fontStyle: 'italic',
    color: 'rgb(255, 255, 255)',
  },
  listItem: {
    marginLeft: 20,
    marginBottom: 5,
    color: 'rgb(255, 255, 255)',
  },
  paragraph: {
    marginBottom: 10,
    color: 'rgb(255, 255, 255)',
  }
});

// Create PDF Document Component
const BrandPDF = ({ brand }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerLeft}>Narrative OS™</Text>
        <Text style={styles.headerRight}>{brand.tagline}</Text>
      </View>

      {/* Brand Manifesto */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Brand Manifesto</Text>
        <Text style={styles.sectionContent}>{brand.brandManifesto}</Text>
      </View>

      {/* Positioning Statement */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Positioning Statement</Text>
        <Text style={styles.sectionContent}>{brand.positioningStatement}</Text>
      </View>

      {/* Audience Matrix */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Audience Matrix</Text>
        <Text style={styles.sectionContent}>{brand.audienceMatrix}</Text>
      </View>

      {/* Tone & Mood Profile */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Tone & Mood Profile</Text>
        <Text style={styles.sectionContent}>
          {typeof brand.toneMoodProfile === 'object' 
            ? brand.toneMoodProfile.description || 'No description available'
            : brand.toneMoodProfile}
        </Text>
        {typeof brand.toneMoodProfile === 'object' && (
          <View style={{ marginTop: 10 }}>
            <Text style={styles.sectionContent}>
              Primary Color: {brand.toneMoodProfile['primary-color'] || 'N/A'}
            </Text>
            <Text style={styles.sectionContent}>
              Secondary Color: {brand.toneMoodProfile['secondary-color'] || 'N/A'}
            </Text>
            <Text style={styles.sectionContent}>
              Background Color: {brand.toneMoodProfile['background-color'] || 'N/A'}
            </Text>
          </View>
        )}
      </View>

      {/* Visual Vibe Summary */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Visual Vibe Summary</Text>
        <Text style={styles.sectionContent}>{brand.visualVibeSummary}</Text>
      </View>

      {/* Footer */}
      <View style={styles.footer}>
        <Text>Page 1 of 1</Text>
        <Text>© {new Date().getFullYear()} Narrative OS. All rights reserved.</Text>
      </View>
    </Page>
  </Document>
);

function Portfolio({ initialProject = null, startOnLastSlide = false }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Initialize all state variables
  const [project, setProject] = useState(initialProject);
  const videoRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(startOnLastSlide ? data.length - 1 : 0);
  const [isLastSubmitted, setIsLastSubmitted] = useState(startOnLastSlide);
  const [finalVideo, setFinalVideo] = useState(startOnLastSlide ? '/assets/video/vid16.mp4' : '');
  const [showExampleSlide, setShowExampleSlide] = useState(startOnLastSlide);
  const [startOnExampleSlide, setStartOnExampleSlide] = useState(initialProject !== null);
  const [helloTextState, setHelloTextState] = useState(startOnLastSlide ? 'hidden' : 'hidden');
  const [sliderFullWidth, setSliderFullWidth] = useState(false);
  const [showWhiteDiv, setShowWhiteDiv] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isAnimatingOut, setIsAnimatingOut] = useState(startOnLastSlide);
  const [rightSideExpanded, setRightSideExpanded] = useState(startOnLastSlide);
  const [isExpanding, setIsExpanding] = useState(false);
  const [showContent, setShowContent] = useState(startOnLastSlide);
  const [uploadWarning, setUploadWarning] = useState(false);
  const [generateClicked, setGenerateClicked] = useState(false);
  
  // Initialize other state variables for different input types
  const [checkboxSelections, setCheckboxSelections] = useState({});
  const [radioSelections, setRadioSelections] = useState({});
  const [multiSelectRadioSelections, setMultiSelectRadioSelections] = useState({});
  const [sliderValues, setSliderValues] = useState({});
  const [multipleTextValues, setMultipleTextValues] = useState({});
  const [promptValues, setPromptValues] = useState({});  // Initialize as an object
  const [uploadedImages, setUploadedImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState({});
  const [failedImages, setFailedImages] = useState({});

  // Text element reference
  const helloTextRef = useRef(null);
  const whiteDivRef = useRef(null);
  // Example slide reference
  const exampleSlideRef = useRef(null);
  // MutationObserver reference
  const observerRef = useRef(null);
  
  // Animation frame reference
  const animationFrameRef = useRef(null);
  // State for glass effect
  const [animationOffset, setAnimationOffset] = useState(0);
  
  // Create a reference for the container
  const workBoxRef = useRef(null);
  
  // Add state for image loading
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [preloadedImageUrl, setPreloadedImageUrl] = useState(null);
  // Add state for retry loading
  const [isRetryLoading, setIsRetryLoading] = useState(false);
  // Add state for retry error message
  const [retryError, setRetryError] = useState(null);
  // Add state for retry error animation
  const [retryErrorAnimation, setRetryErrorAnimation] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(0);
  const [isBrandSwitch, setIsBrandSwitch] = useState(false);
  const [showDownloadTooltip, setShowDownloadTooltip] = useState(false);
  const [isDownloadLoading, setIsDownloadLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const messages = [
    "Building Your Brand Blueprint...",
    "Distilling your vision...",
    "Crafting your narrative...",
    "Finalizing your brand identity..."
  ];

  // Add this with other state declarations at the top
  const [isInitialGeneration, setIsInitialGeneration] = useState(true);
  const [selectedBrandIndex, setSelectedBrandIndex] = useState(0);
  // Add this with other state declarations at the top
  const [showOverlay, setShowOverlay] = useState(false);
  const [activeSection, setActiveSection] = useState(null);

  // Add this with other state declarations at the top
  const [modifiedInputs, setModifiedInputs] = useState({
    Story: false,
    Talk: false,
    BBQ: false,
    Look: false,
    Game: false,
    Image: false
  });

  // Add this with other state declarations at the top
  const [isMobile, setIsMobile] = useState(false);

  // Add showSurveyPopup state at the top with other state declarations
  const [showSurveyPopup, setShowSurveyPopup] = useState(false);

  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const [expandedSection, setExpandedSection] = useState('positioning');

  const [hasModifiedText, setHasModifiedText] = useState(false);

  const formatMarkdownText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .replace(/^# (.*$)/gm, '<h4>$1</h4>')           // h1 headings
      .replace(/^## (.*$)/gm, '<h5>$1</h5>')          // h2 headings
      .replace(/^### (.*$)/gm, '<h6>$1</h6>')         // h3 headings
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')  // Bold text
      .replace(/\*(.*?)\*/g, '<em>$1</em>')             // Italic text
      .replace(/\n/g, '<br />');                        // Line breaks
  };

  useEffect(() => {
    const checkMobile = () => {
      // console.log("window.innerWidth:", window.innerWidth);
      setIsMobile(window.innerWidth <= 600);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    // console.log("isMobile:", isMobile);
  }, [isMobile]);

  // Add this function to handle input changes
  const handleInputModification = (section) => {
    setModifiedInputs(prev => ({
      ...prev,
      [section]: true
    }));
  };

  // Add effect for cycling messages
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingMessage((prev) => (prev + 1) % messages.length);
      }, 3000); // Change message every 3 seconds
      return () => clearInterval(interval);
    }
  }, [isLoading]);

  // Add initial authentication check with polling
  useEffect(() => {
    let pollInterval;
    let timeoutId;

    const checkAuthState = () => {
      const currentUser = auth.currentUser;
      const storedAuth = localStorage.getItem('isAuthenticated') === 'true';
      const isAuthenticated = currentUser || storedAuth;
      
      // console.log('Auth check:', {
      //   currentUser: !!currentUser,
      //   storedAuth,
      //   isAuthenticated
      // });

      if (isAuthenticated) {
        // console.log('User is authenticated, setting UI state...');
        setIsAnimatingOut(true);
        setRightSideExpanded(true);
        // Clear any existing intervals/timeouts
        if (pollInterval) clearInterval(pollInterval);
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    // Initial check
    checkAuthState();

    // Set up polling every 2 seconds for up to 10 seconds
    let attempts = 0;
    pollInterval = setInterval(() => {
      attempts++;
      checkAuthState();
      
      if (attempts >= 5) { // Stop after 5 attempts (10 seconds)
        clearInterval(pollInterval);
      }
    }, 2000);

    // Also set up a one-time check after 5 seconds
    timeoutId = setTimeout(checkAuthState, 5000);

    // Cleanup
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    // Initialize the video refs array
    videoRefs.current = videoRefs.current.slice(0, data.length);
    
    // Initialize videos
    videoRefs.current.forEach((videoEl, index) => {
      if (videoEl) {
        // Add error handling for video loading
        videoEl.onerror = (e) => {
          // console.error('Video loading error:', e);
          // If the video fails to load, try to show a fallback image or message
          const container = videoEl.parentElement;
          if (container) {
            container.style.background = 'rgba(0, 0, 0, 0.5)';
            container.innerHTML = `
              <div style="
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                color: white;
                text-align: center;
                padding: 20px;
                background: rgba(0, 0, 0, 0.7);
                border-radius: 10px;
              ">
                <p>Unable to load video</p>
                <p style="font-size: 0.8em; opacity: 0.7;">Please check your connection and try again</p>
              </div>
            `;
          }
        };

        // Add loading handling
        videoEl.onloadeddata = () => {
          // console.log(`Video ${index} loaded successfully`);
        };

        // Try to play the video
        videoEl.play().catch(err => {
          // console.log('Video autoplay failed:', err);
          // If autoplay fails, we'll still show the video but it won't autoplay
          videoEl.muted = true; // Try muted autoplay as fallback
          videoEl.play().catch(e => {
            // console.log('Muted autoplay also failed:', e);
          });
        });
      }
    });
  }, []);

  // Effect to handle the animation when final state is triggered
  useEffect(() => {
    if (startOnExampleSlide && workBoxRef.current) {
      // Force reflow to ensure the animation applies
      const lastIndex = data.length - 1;
      const items = document.querySelectorAll('.work-boxs .item');
      
      if (items.length > 0) {
        // First make other items tiny
        items.forEach((item, i) => {
          item.style.width = '0';
          item.style.margin = '0';
          item.style.opacity = '0';
        });
        
        // Then expand the last item after a short delay
        setTimeout(() => {
          if (items[lastIndex]) {
            items[lastIndex].style.width = '100%';
            items[lastIndex].style.height = '90vh';
            
            // Load the new video
            if (videoRefs.current[lastIndex]) {
              videoRefs.current[lastIndex].src = finalVideo;
              videoRefs.current[lastIndex].load();
              videoRefs.current[lastIndex].play().catch(err => {
                console.log('Video play failed:', err);
              });
            }
            
            // After 6 seconds, add the example slide
            setTimeout(() => {
              setHelloTextState('hidden'); // Reset text state
              setShowExampleSlide(true);
              
              // Wait for the DOM to update with the new slide
              setTimeout(() => {
                // Get all items including the new one
                const allItems = document.querySelectorAll('.work-boxs .item');
                const exampleSlide = document.querySelector('.example-slide');
                const lastSlide = items[lastIndex];
                
                if (lastSlide && exampleSlide) {
                  // Animate the last slide away
                  lastSlide.style.width = '0';
                  lastSlide.style.margin = '0';
                  lastSlide.style.opacity = '0';
                  
                  // Animate the example slide in
                  exampleSlide.style.width = '100%';
                  exampleSlide.style.height = '90vh';
                  exampleSlide.style.opacity = '1'; // Ensure opacity is 1

                  // Show the hello text after a short delay
                  setTimeout(() => {
                    setHelloTextState('visible');
                    
                    // Show the white div (authentication form) after the hello text animation
                    setTimeout(() => {
                      setShowWhiteDiv(true);
                    }, 0);
                  }, 0);
                }
              }, 0);
            }, 0);
          }
        }, 0);
      }
    } else if (isLastSubmitted && workBoxRef.current) {
      // Force reflow to ensure the animation applies
      const lastIndex = data.length - 1;
      const items = document.querySelectorAll('.work-boxs .item');
      
      if (items.length > 0) {
        // First make other items tiny
        items.forEach((item, i) => {
          if (i !== lastIndex) {
            item.style.width = '0';
            item.style.margin = '0';
            item.style.opacity = '0';
          }
        });
        
        // Then expand the last item after a short delay
        setTimeout(() => {
          if (items[lastIndex]) {
            items[lastIndex].style.width = '100%';
            items[lastIndex].style.height = '90vh';
            
            // Load the new video
            if (videoRefs.current[lastIndex]) {
              videoRefs.current[lastIndex].src = finalVideo;
              videoRefs.current[lastIndex].load();
              videoRefs.current[lastIndex].play().catch(err => {
                console.log('Video play failed:', err);
              });
            }
            
            // After 6 seconds, add the example slide
            setTimeout(() => {
              setHelloTextState('hidden'); // Reset text state
              setShowExampleSlide(true);
              
              // Wait for the DOM to update with the new slide
              setTimeout(() => {
                // Get all items including the new one
                const allItems = document.querySelectorAll('.work-boxs .item');
                const exampleSlide = document.querySelector('.example-slide');
                const lastSlide = items[lastIndex];
                
                if (lastSlide && exampleSlide) {
                  // Animate the last slide away
                  lastSlide.style.width = '0';
                  lastSlide.style.margin = '0';
                  lastSlide.style.opacity = '0';
                  
                  // Animate the example slide in
                  exampleSlide.style.width = '100%';
                  exampleSlide.style.height = '90vh';
                  exampleSlide.style.opacity = '1'; // Ensure opacity is 1

                  // Show the hello text after a short delay
                  setTimeout(() => {
                    setHelloTextState('visible');
                    
                    // Show the white div (authentication form) after the hello text animation
                    setTimeout(() => {
                      setShowWhiteDiv(true);
                    }, 1000);
                  }, 500);
                }
              }, 50);
            }, 6000);
          }
        }, 100);
      }
    }
  }, [isLastSubmitted, finalVideo, data.length]);
  
  // Show content for first slide on initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 800); // Match the expansion animation duration

    return () => clearTimeout(timer);
  }, []); // Run only on mount

  // Handle content animations when activeIndex changes
  useEffect(() => {
    if (activeIndex !== null) {
      setIsExpanding(true);
      setShowContent(false);
      
      // Dispatch slide change event
      const event = new CustomEvent('slideChange', { detail: activeIndex });
      document.dispatchEvent(event);
      
      const timer = setTimeout(() => {
        setShowContent(true);
        setIsExpanding(false);
      }, 800);
      
      return () => clearTimeout(timer);
    }
  }, [activeIndex]);

  // Function to handle clicking on an item
  function handleOpenWork(index) {
    // Don't allow changing if in final state
    if (isLastSubmitted) return;
    setActiveIndex(index);
  }
  
  // Function to handle next slide
  function handleNextSlide(e) {
    e.preventDefault();
    e.stopPropagation(); // Prevent the parent onClick from firing
    
    // If we're in the final state, don't change slides
    if (isLastSubmitted) return;
    
    // Calculate next index with wrap-around
    const nextIndex = (activeIndex + 1) % data.length;
    setActiveIndex(nextIndex);
  }
  
  // Handle input change for a specific slide
  function handleInputChange(index, inputIndex, value) {
    console.log('Input changed:', { index, inputIndex, value, currentPromptValues: promptValues });
    setPromptValues(prev => {
      const newValues = { ...prev };
      newValues[`${index}-${inputIndex}`] = value;
      console.log('New prompt values:', newValues);
      return newValues;
    });
  }
  
  // Function to handle form submission for a specific slide
  async function handleSubmit(e, index) {
    e.preventDefault();
    
    // Check if this is the last prompt in the data array
    const isLastPrompt = index === data.length - 1;
    
    if (isLastPrompt) {
      try {
        // Get current user if signed in
        const currentUser = auth.currentUser;
        
        // Store authentication state in localStorage
        localStorage.setItem('isAuthenticated', currentUser ? 'true' : 'false');
        
        // Create project data from all the collected information
        const projectData = {
          userId: currentUser?.uid || null,
          title: promptValues['0-0'] || 'Untitled Project',
          description: promptValues['1-0'] || '',
          images: uploadedImages.map(img => ({
            url: img.gcsUrl,
            previewUrl: img.url,
            uploadedAt: new Date().toISOString()
          })),
          brands: [], // Initialize empty brands array
          settings: {
            // Save all input types from works-onboarding.json
            checkboxSelections: Object.entries(checkboxSelections).reduce((acc, [slideIndex, selections]) => ({
              ...acc,
              [slideIndex]: selections
            }), {}),
            radioSelections: Object.entries(radioSelections).reduce((acc, [slideIndex, selection]) => ({
              ...acc,
              [slideIndex]: selection
            }), {}),
            multiSelectRadioSelections: multiSelectRadioSelections,
            sliderValues: Object.entries(sliderValues).reduce((acc, [slideIndex, values]) => ({
              ...acc,
              [slideIndex]: values
            }), {}),
            multipleTextValues: Object.entries(multipleTextValues).reduce((acc, [slideIndex, values]) => ({
              ...acc,
              [slideIndex]: values
            }), {}),
            promptValues: Object.entries(promptValues).map(([key, value]) => {
              const [slideIndex, inputIndex] = key.split('-');
              return {
                slideIndex: parseInt(slideIndex),
                inputIndex: parseInt(inputIndex),
                value: value,
                question: data[parseInt(slideIndex)]?.question,
                type: data[parseInt(slideIndex)]?.type
              };
            })
          },
          // Add metadata about the onboarding process
          onboardingMetadata: {
            totalSteps: data.length,
            completedAt: new Date(),
            version: '1.0'
          }
        };

        // Create the project in Firestore
        const createdProject = await createProject(projectData);
        
        // Set the project state
        setProject(createdProject);

        // Store project ID in localStorage for anonymous users
        if (!currentUser) {
          localStorage.setItem('lastProjectId', createdProject.id);
        }

        // Update URL to reflect the new project ID without refreshing
        window.history.replaceState({}, '', `/brands/${createdProject.id}`);

        // Show the example slide after a delay
        setTimeout(() => {
          // Trigger the final animation and video change immediately
          setFinalVideo('/assets/video/vid16.mp4');
          setIsLastSubmitted(true);
        }, 1500);

        // Show the example slide after a delay
        setTimeout(() => {
          // Check authentication state from localStorage
          const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
          
          if (isAuthenticated) {
            if(isMobile){
              // do nothing
            }else{
              setShowExampleSlide(true);
              setHelloTextState('hidden');
              setShowWhiteDiv(false);
              setIsAnimatingOut(true);
              setRightSideExpanded(true);
              setShowOverlay(false);
            }
          } else {
            if(isMobile){
              // do nothing
            }else{
              // For unauthenticated users, show the full auth UI
              setShowExampleSlide(true);
              setHelloTextState('visible');
              setShowWhiteDiv(true);
            }
          }
        }, 6000);

        if (auth?.currentUser) {
          // Generate brand assets in the background
          (async () => {
            try {
              // Generate overview first
              const overview = await generateOverview();
              console.log('Generated overview:', overview);

              // Generate all brand elements in parallel using the overview
              const [
                brandManifesto,
                positioningStatement,
                audienceMatrix,
                toneMoodProfile,
                visualVibeSummary,
                tagline,
                image
              ] = await Promise.all([
                generateBrandManifesto(overview),
                generatePositioningStatement(overview),
                generateAudienceMatrix(overview),
                generateToneMoodProfile(overview),
                generateVisualVibeSummary(overview),
                generateTagline(overview),
                generateImage(overview)
              ]);

              if (!image || !brandManifesto || !audienceMatrix) {
                // Instead of throwing, show toast and reset state
                showToast('Brand Blueprint generation failed');
                setActiveIndex(0);
                setShowExampleSlide(false);
                setGenerateClicked(false);
                setIsLastSubmitted(false);
                setFinalVideo('');
                setShowContent(true);
                return;
              }

              // Only update if we have at least one successful response
              if (tagline || image) {
                const updateData = {
                  brands: [{
                    tagline: tagline || 'Brand tagline generation failed',
                    imageUrl: image || null,
                    brandManifesto: brandManifesto || 'Brand manifesto generation failed',
                    positioningStatement: positioningStatement || 'Positioning statement generation failed',
                    audienceMatrix: audienceMatrix || 'Audience matrix generation failed',
                    toneMoodProfile: toneMoodProfile || 'Tone and mood profile generation failed',
                    visualVibeSummary: visualVibeSummary || 'Visual vibe summary generation failed',
                    generatedAt: new Date().toISOString()
                  }]
                };
                
                try {
                  const updatedProject = await updateProject(createdProject.id, updateData);
                  setProject(updatedProject);

                  // Reset all modified states after fresh brand generation
                  setModifiedInputs({
                    Story: false,
                    Talk: false,
                    BBQ: false,
                    Look: false,
                    Game: false,
                    Image: false
                  });

                  // Preload the image
                  if (updatedProject.brands?.[0]?.imageUrl) {
                    const img = new Image();
                    img.onload = () => {
                      setIsImageLoaded(true);
                      setPreloadedImageUrl(updatedProject.brands[0].imageUrl);
                    };
                    img.onerror = () => {
                      setIsImageLoaded(false);
                      setPreloadedImageUrl(null);
                    };
                    img.src = updatedProject.brands[0].imageUrl;
                  }

                  // Refresh the page after creating the brand to ensure clean state
                  window.location.reload();
                } catch (updateError) {
                  // Instead of throwing, show toast and reset state
                  showToast('Failed to update project with brand assets');
                  setActiveIndex(0);
                  setShowExampleSlide(false);
                  setGenerateClicked(false);
                  setIsLastSubmitted(false);
                  setFinalVideo('');
                  setShowContent(true);
                  return;
                }
              } else {
                // Instead of throwing, show toast and reset state
                showToast('No brand assets were generated successfully');
                setActiveIndex(0);
                setShowExampleSlide(false);
                setGenerateClicked(false);
                setIsLastSubmitted(false);
                setFinalVideo('');
                setShowContent(true);
                return;
              }
            } catch (error) {
              console.error('Error in brand generation process:', error);
              // Show the error message from the API if available, otherwise show the generic message
              const errorMessage = error.message || 'There was an issue with your information, please change and try again';
              showToast(errorMessage);
              // Reset to first slide
              setActiveIndex(0);
              setShowExampleSlide(false);
              setGenerateClicked(false);
              setIsLastSubmitted(false);
              setFinalVideo('');
              setShowContent(true);
            }
          })();
        }else{
          setTimeout(() => {
            setAuthPopUp(true);
          }, 7000);
        }
      } catch (error) {
        console.error('Error creating project:', error);
        showToast('There was an issue with your information, please change and try again');
        // Reset to first slide
        setActiveIndex(0);
        setShowExampleSlide(false);
        setGenerateClicked(false);
        setIsLastSubmitted(false);
        setFinalVideo('');
        setShowContent(true);
      }
    } else {
      // Move to next slide for non-final prompts
      handleNextSlide(e);
    }
  }
  
  // Function to handle Google sign-in
  const handleGoogleSignIn = async () => {
    if (!auth || !googleProvider) {
      setError('Firebase authentication is not properly initialized. Please check your environment variables.');
      trackError('auth_init_error', 'Firebase authentication not initialized');
      return;
    }

    try {
      setIsGoogleLoading(true);
      setError(null);
      // console.log('Starting Google sign-in process...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      // console.log('Successfully signed in with Google:', user.uid);
      
      // Check if we have a project to associate
      const projectId = localStorage.getItem('lastProjectId');
      if (projectId) {
        // console.log('Found project to associate:', projectId);
        try {
          await associateProjectWithUser(projectId, user.uid);
          // console.log('Successfully associated project with user');
          localStorage.removeItem('lastProjectId');
        } catch (error) {
          console.error('Error associating project with user:', error);
        }
      }
      
      // Since Google sign-in means the email is already verified,
      // we can directly trigger the same flow as email verification
      setIsVerified(true);
      setVerificationSent(false);
      // Start the reverse animation
      setIsAnimatingOut(true);
      // After fade out completes, expand right side
      setTimeout(() => {
        setAuthPopUp(false);
        setRightSideExpanded(true);

        // Now trigger the retry generation
        handleRetryGeneration(true);
      }, 0);
      
      // Track successful sign in
      trackSignIn('google');
      
    } catch (error) {
      console.error('Error signing in with Google:', error);
      setError(error.message);
      trackError('google_signin_error', error.message);
    } finally {
      setIsGoogleLoading(false);
    }
  };
  
  // Function to handle email/password sign up
  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setError(null);
    setIsEmailLoading(true);

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      setIsEmailLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      setIsEmailLoading(false);
      return;
    }

    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Send verification email
      await sendEmailVerification(user, actionCodeSettings);
      
      // Show verification sent message
      setVerificationSent(true);

      // Track sign up
      trackSignUp('email');

    } catch (error) {
      console.error('Error during sign up:', error);
      setError(error.message);
      trackError('email_signup_error', error.message);
    } finally {
      setIsEmailLoading(false);
    }
  };
  
  // Add polling for verification status
  useEffect(() => {
    let intervalId;
    
    if (verificationSent && !isVerified) {
      // console.log('Starting verification status polling');
      intervalId = setInterval(async () => {
        try {
          const user = auth.currentUser;
          if (user) {
            // console.log('Checking verification status...');
            // Force refresh the user to get latest verification status
            await user.reload();
            if (user.emailVerified) {
              console.log('Email verified!');
              
              // Check if we have a project to associate
              const projectId = localStorage.getItem('lastProjectId');
              if (projectId) {
                console.log('Found project to associate:', projectId);
                try {
                  await associateProjectWithUser(projectId, user.uid);
                  console.log('Successfully associated project with user');
                  localStorage.removeItem('lastProjectId');
                } catch (error) {
                  console.error('Error associating project with user:', error);
                }
              }

              setIsVerified(true);
              setVerificationSent(false);
              // Start the reverse animation
              setIsAnimatingOut(true);
              // After fade out completes, expand right side and trigger retry generation
              setTimeout(() => {
                setAuthPopUp(false);
                setRightSideExpanded(true);
                handleRetryGeneration(true);
              }, 0);
              // Clear the interval once verified
              clearInterval(intervalId);
            } else {
              console.log('Email not yet verified');
            }
          } else {
            console.log('No user found during verification check');
          }
        } catch (error) {
          console.error('Error checking verification status:', error);
        }
      }, 2000); // Check every 2 seconds
    }

    return () => {
      if (intervalId) {
        console.log('Cleaning up verification polling');
        clearInterval(intervalId);
      }
    };
  }, [verificationSent, isVerified]);
  
  // Handle checkbox changes
  const handleCheckboxChange = (slideIndex, option) => {
    setCheckboxSelections(prev => {
      const currentSelections = prev[slideIndex] || [];
      const maxSelections = data[slideIndex].inputs[0].maxSelections;
      
      // Handle both string and object options
      const optionValue = typeof option === 'string' ? option : option.mainText;
      
      if (currentSelections.includes(optionValue)) {
        return {
          ...prev,
          [slideIndex]: currentSelections.filter(item => item !== optionValue)
        };
      } else if (currentSelections.length < maxSelections) {
        return {
          ...prev,
          [slideIndex]: [...currentSelections, optionValue]
        };
      }
      return prev;
    });
  };

  // Handle radio changes
  const handleRadioChange = (slideIndex, inputIndex, value, maxSelections = 1) => {
    // Handle both string and object values - use key if available, otherwise use the value as-is
    const optionKey = typeof value === 'object' && value.key ? value.key : value;
    const optionText = typeof value === 'object' && value.mainText ? value.mainText : value;
    
    if (maxSelections === 1) {
      // Single select radio - use existing logic
      setRadioSelections(prev => {
        const newSelections = {
          ...prev,
          [`${slideIndex}-${inputIndex}`]: optionKey
        };
        console.log('New radio selections:', newSelections);
        return newSelections;
      });
    } else {
      // Multi-select radio - use checkbox-like logic
      setMultiSelectRadioSelections(prev => {
        const currentSelections = prev[`${slideIndex}-${inputIndex}`] || [];
        
        if (currentSelections.includes(optionKey)) {
          // Remove if already selected
          return {
            ...prev,
            [`${slideIndex}-${inputIndex}`]: currentSelections.filter(item => item !== optionKey)
          };
        } else if (currentSelections.length < maxSelections) {
          // Add if under max limit
          return {
            ...prev,
            [`${slideIndex}-${inputIndex}`]: [...currentSelections, optionKey]
          };
        }
        return prev; // Don't add if at max limit
      });
    }
    
    // Handle conditional text inputs
    const slide = data[slideIndex];
    if (slide.type === 'mixed') {
      // Find conditional text inputs that are associated with this radio input
      const conditionalInputs = [];
      // Normalize options to their string values (support string or { key, mainText, subText })
      const optionValues = (slide.inputs[inputIndex]?.options || []).map(opt =>
        typeof opt === 'string' ? opt : (opt?.key || opt?.mainText)
      );
      
      if (maxSelections > 1) {
        // For multi-select radio, only look for text inputs that come immediately after this radio input
        let nextInputIndex = inputIndex + 1;
        while (nextInputIndex < slide.inputs.length) {
          const nextInput = slide.inputs[nextInputIndex];
          
          // If we hit another radio input, stop looking
          if (nextInput.type === 'radio') {
            break;
          }
          
          // If this is a conditional text input that matches our radio options, add it
          if (nextInput.type === 'text' && 
              nextInput.conditionalOn && 
              optionValues.includes(nextInput.conditionalOn)) {
            conditionalInputs.push(nextInput);
          }
          
          nextInputIndex++;
        }
      } else {
        // For single-select radio, find ALL text inputs in the slide that match this radio's options
        slide.inputs.forEach((textInput, textInputIndex) => {
          if (textInput.type === 'text' && 
              textInput.conditionalOn && 
              optionValues.includes(textInput.conditionalOn)) {
            conditionalInputs.push(textInput);
          }
        });
      }
      console.log('Found conditional inputs for radio:', conditionalInputs);
      
      if (conditionalInputs.length > 0) {
        // Get current prompt values
        setPromptValues(prev => {
          const newValues = { ...prev };
          
          conditionalInputs.forEach(conditionalInput => {
            const textInputIndex = slide.inputs.indexOf(conditionalInput);
            const key = `${slideIndex}-${textInputIndex}`;
            
            console.log('Processing conditional input:', {
              conditionalOn: conditionalInput.conditionalOn,
              currentRadioValue: value,
              textInputIndex,
              key,
              currentValue: newValues[key],
              maxSelections
            });
            
            if (maxSelections === 1) {
              // Single select radio - clear all other conditional text inputs for this radio
              if (conditionalInput.conditionalOn !== optionKey) {
                console.log('Clearing value for unselected radio option:', conditionalInput.conditionalOn);
                delete newValues[key];
              }
            } else {
              // Multi-select radio - only clear if this specific option was deselected
              const currentSelections = multiSelectRadioSelections[`${slideIndex}-${inputIndex}`] || [];
              if (!currentSelections.includes(conditionalInput.conditionalOn)) {
                console.log('Clearing value for deselected multi-select option:', conditionalInput.conditionalOn);
                delete newValues[key];
              }
            }
          });
          
          console.log('Final prompt values:', newValues);
          return newValues;
        });
      }
    }
  };

  // Handle slider changes
  const handleSliderChange = (slideIndex, sliderIndex, value) => {
    setSliderValues(prev => ({
      ...prev,
      [slideIndex]: {
        ...(prev[slideIndex] || {}),
        [sliderIndex]: value
      }
    }));
  };

  // Handle multiple text input changes
  const handleMultipleTextChange = (slideIndex, inputIndex, value) => {
    setMultipleTextValues(prev => ({
      ...prev,
      [slideIndex]: {
        ...(prev[slideIndex] || {}),
        [inputIndex]: value
      }
    }));
  };

  const showUploadWarning = () => {
    setUploadWarning(true);
    setTimeout(() => {
      setUploadWarning(false);
    }, 3000);
  };

  const handleImageUpload = async (e) => {
    console.log('Starting image upload process...');
    const files = Array.from(e.target.files);
    console.log(`Number of files to upload: ${files.length}`);

    // Check if adding these files would exceed the limit
    if (uploadedImages.length + files.length > 5) {
      showUploadWarning();
      return;
    }

    // Create immediate previews and set loading state
    const newImages = files.map(file => ({
      url: URL.createObjectURL(file),
      gcsUrl: null,
      id: Math.random().toString(36).substr(2, 9)
    }));

    console.log('Created new image objects:', newImages);

    setUploadedImages(prev => [...prev, ...newImages]);
    setLoadingImages(prev => ({
      ...prev,
      ...newImages.reduce((acc, img) => ({ ...acc, [img.id]: true }), {})
    }));

    // Process each file
    for (const [index, file] of files.entries()) {
      try {
        console.log(`Processing file ${index + 1}/${files.length}:`, {
          name: file.name,
          size: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
          type: file.type
        });
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'image');
        console.log('FormData created with file and type');

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        const response = await fetch(`${API_URL}/upload`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
          headers: {
            ...getAuthHeaders()
          }
        });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to upload image: ${response.status} ${errorText}`);
        }

        const data = await response.json();
        console.log('Server response data:', {
          status: response.status,
          url: data.url,
          type: typeof data.url
        });
        
        // Get signed URL for the GCS URL
        console.log('Getting signed URL for:', data.url);
        const signedUrl = await getFreshSignedUrl(data.url);
        console.log('Received signed URL:', signedUrl);
        
        // Update the image with both GCS URL and signed URL
        setUploadedImages(prev => {
          const updated = prev.map(img => 
            img.id === newImages[index].id 
              ? { ...img, gcsUrl: data.url, url: signedUrl || data.url }
              : img
          );
          console.log('Updated uploaded images:', updated);
          return updated;
        });
        
        setLoadingImages(prev => {
          const newState = { ...prev };
          delete newState[newImages[index].id];
          return newState;
        });

      } catch (error) {
        console.error('Error in image upload process:', error);
        // Mark this image as failed
        setFailedImages(prev => ({
          ...prev,
          [newImages[index].id]: true
        }));
        setLoadingImages(prev => {
          const newState = { ...prev };
          delete newState[newImages[index].id];
          return newState;
        });
      }
    }
  };

  // Add debounce to drag events
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    
    // Check if adding these files would exceed the limit
    if (uploadedImages.length + files.length > 5) {
      showUploadWarning();
      return;
    }

    handleImageUpload({ target: { files } });
  };

  // Remove uploaded image
  const removeImage = (id) => {
    setUploadedImages(prev => {
      const newImages = prev.filter(img => img.id !== id);
      const removedImage = prev.find(img => img.id === id);
      if (removedImage) {
        URL.revokeObjectURL(removedImage.url);
      }
      return newImages;
    });
    setLoadingImages(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
    setFailedImages(prev => {
      const newState = { ...prev };
      delete newState[id];
      return newState;
    });
  };

  // Render input based on type
  const renderInput = (slide, index, onModify) => {
    switch (slide.type) {
      case 'text':
        return (
          <div className="input-group">
            <input
              type="text"
              value={promptValues[index]}
              onChange={(e) => {
                handleInputChange(index, e.target.value);
                onModify?.();
              }}
              placeholder={slide.placeholder}
              className="form-control"
            />
          </div>
        );

      case 'mixed':
        return (
          <div className="mixed-inputs">
            {slide.inputs.map((input, inputIndex) => {
              if (input.type === 'checkbox') {
                return (
                  <div key={inputIndex} className="checkbox-group">
                    <label style={{
                      display: 'block',
                      marginBottom: '5px',
                      color: '#fff',
                      fontSize: '16px',
                      fontWeight: '500'
                    }}>{input.label}</label>
                    {input.maxSelections && input.maxSelections > 1 && (
                      <div style={{
                        marginBottom: '15px',
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '12px'
                      }}>
                        Select up to {input.maxSelections} option{input.maxSelections > 1 ? 's' : ''}
                      </div>
                    )}
                    <div className="checkbox-options" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '12px'
                    }}>
                      {input.options.map((option, optionIndex) => {
                        const optionValue = typeof option === 'string' ? option : option.mainText;
                        return (
                        <label key={optionIndex} style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '12px 16px',
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          ':hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          }
                        }}>
                          <div style={{
                            position: 'relative',
                            width: '20px',
                            height: '20px',
                            minWidth: '20px',
                            borderRadius: '4px',
                            border: '2px solid rgba(255, 255, 255, 0.3)',
                            backgroundColor: (checkboxSelections[index] || []).includes(optionValue) ? '#fd5b38' : 'transparent',
                            transition: 'all 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {(checkboxSelections[index] || []).includes(optionValue) && (
                              <svg 
                                width="12" 
                                height="12" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="white" 
                                strokeWidth="3" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                                style={{
                                  opacity: 1,
                                  transition: 'opacity 0.2s ease'
                                }}
                              >
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                          </div>
                          <span style={{
                            color: '#fff',
                            fontSize: '14px',
                            userSelect: 'none'
                          }}>{optionValue}</span>
                          <input
                            type="checkbox"
                            checked={(checkboxSelections[index] || []).includes(optionValue)}
                            onChange={() => {
                              handleCheckboxChange(index, option);
                              onModify?.();
                            }}
                            style={{
                              position: 'absolute',
                              opacity: 0,
                              cursor: 'pointer',
                              height: 0,
                              width: 0
                            }}
                          />
                        </label>
                        );
                      })}
                    </div>
                  </div>
                );
              } else if (input.type === 'radio') {
                const maxSelections = input.maxSelections || 1;
                const isMultiSelect = maxSelections > 1;
                
                return (
                  <div key={inputIndex} className="radio-group">
                    <div>
                      <label style={{
                        display: 'block',
                        marginBottom: '5px',
                        color: '#fff',
                        fontSize: '16px',
                        fontWeight: '500'
                      }}>{input.label}</label>
                      {input.subLabel && (
                        <label style={{
                          display: 'block',
                          marginBottom: '15px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>{input.subLabel}</label>
                      )}
                      {isMultiSelect && (
                        <div style={{
                          marginBottom: '15px',
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '12px'
                        }}>
                          Select up to {maxSelections} option{maxSelections > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>
                    <div className="radio-options" style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '12px'
                    }}>
                      {input.options.map((option, optionIndex) => {
                        const optionLabel = typeof option === 'string' ? option : option?.mainText;
                        const optionSubText = typeof option === 'string' ? undefined : option?.subText;
                        const optionKey = typeof option === 'string' ? option : (option?.key || option?.mainText);
                        const optionMainText = typeof option === 'string' ? option : option?.mainText;
                        const isSelected = isMultiSelect 
                          ? (multiSelectRadioSelections[`${index}-${inputIndex}`] || []).includes(optionKey)
                          : radioSelections[`${index}-${inputIndex}`] === optionKey;
                        
                        return (
                          <label key={optionIndex} style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '10px',
                            padding: '12px 16px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            ':hover': {
                              backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                          }}>
                            <div style={{
                              position: 'relative',
                              width: '20px',
                              height: '20px',
                              minWidth: '20px',
                              borderRadius: isMultiSelect ? '4px' : '50%',
                              border: '2px solid rgba(255, 255, 255, 0.3)',
                              backgroundColor: isSelected ? '#fd5b38' : 'transparent',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {isSelected && (
                                isMultiSelect ? (
                                  <svg 
                                    width="12" 
                                    height="12" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="white" 
                                    strokeWidth="3" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    style={{
                                      opacity: 1,
                                      transition: 'opacity 0.2s ease'
                                    }}
                                  >
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                  </svg>
                                ) : (
                                  <div style={{
                                    width: '10px',
                                    height: '10px',
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    transition: 'all 0.2s ease'
                                  }} />
                                )
                              )}
                            </div>
                            <div style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: optionSubText ? '2px' : 0
                            }}>
                              <span style={{
                                color: '#fff',
                                fontSize: '14px',
                                fontWeight: 600,
                                userSelect: 'none'
                              }}>{optionLabel}</span>
                              {optionSubText && (
                                <span style={{
                                  color: 'rgba(255, 255, 255, 0.8)',
                                  fontSize: '12px',
                                  lineHeight: 1.3,
                                  userSelect: 'none'
                                }}>{optionSubText}</span>
                              )}
                            </div>
                            <input
                              type={isMultiSelect ? "checkbox" : "radio"}
                              name={isMultiSelect ? `checkbox-${index}-${inputIndex}` : `radio-${index}-${inputIndex}`}
                              checked={isSelected}
                              onChange={() => {
                                handleRadioChange(index, inputIndex, option, maxSelections);
                                onModify?.();
                              }}
                              style={{
                                position: 'absolute',
                                opacity: 0,
                                cursor: 'pointer',
                                height: 0,
                                width: 0
                              }}
                            />
                          </label>
                        );
                      })}
                    </div>
                    
                    {/* Conditional text inputs for this radio group */}
                    {(() => {
                      // Find text inputs that have conditionalOn values matching this radio's options
                      // For single-select radio, we need to find ALL text inputs in the slide that match
                      // For multi-select radio, we only look for text inputs that come immediately after
                      const conditionalTextInputs = [];
                      const optionValues = (input.options || []).map(opt => typeof opt === 'string' ? opt : (opt?.key || opt?.mainText));
                      
                      if (isMultiSelect) {
                        // For multi-select radio, only look for text inputs that come immediately after this radio input
                        let nextInputIndex = inputIndex + 1;
                        while (nextInputIndex < slide.inputs.length) {
                          const nextInput = slide.inputs[nextInputIndex];
                          
                          // If we hit another radio input, stop looking
                          if (nextInput.type === 'radio') {
                            break;
                          }
                          
                          // If this is a conditional text input that matches our radio options, add it
                          if (nextInput.type === 'text' && 
                              nextInput.conditionalOn && 
                              optionValues.includes(nextInput.conditionalOn)) {
                            conditionalTextInputs.push(nextInput);
                          }
                          
                          nextInputIndex++;
                        }
                      } else {
                        // For single-select radio, find ALL text inputs in the slide that match this radio's options
                        slide.inputs.forEach((textInput, textInputIndex) => {
                          if (textInput.type === 'text' && 
                              textInput.conditionalOn && 
                              optionValues.includes(textInput.conditionalOn)) {
                            conditionalTextInputs.push(textInput);
                          }
                        });
                      }
                      
                      if (conditionalTextInputs.length === 0) return null;
                      
                      // Render all conditional text inputs that match selected radio options
                      return conditionalTextInputs.map((conditionalTextInput) => {
                        const conditionalInputIndex = slide.inputs.indexOf(conditionalTextInput);
                        const isSelected = isMultiSelect 
                          ? (multiSelectRadioSelections[`${index}-${inputIndex}`] || []).includes(conditionalTextInput.conditionalOn)
                          : radioSelections[`${index}-${inputIndex}`] === conditionalTextInput.conditionalOn;
                        
                        if (!isSelected) return null;
                        
                        return (
                          <div key={`conditional-${conditionalInputIndex}`} className="text-input" style={{
                            marginTop: '20px',
                            marginBottom: '0'
                          }}>
                            <label style={{
                              display: 'block',
                              marginBottom: '8px',
                              color: '#fff',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}>{conditionalTextInput.label}</label>
                            <input
                              type="text"
                              value={promptValues[`${index}-${conditionalInputIndex}`] || ''}
                              onChange={(e) => {
                                console.log('Conditional text input changed:', {
                                  index,
                                  conditionalInputIndex,
                                  value: e.target.value,
                                  currentPromptValues: promptValues
                                });
                                setHasModifiedText(true);
                                handleInputChange(index, conditionalInputIndex, e.target.value);
                                onModify?.();
                              }}
                              placeholder={conditionalTextInput.placeholder}
                              className="form-control"
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '6px',
                                color: '#fff',
                                fontSize: '14px',
                                outline: 'none',
                                transition: 'all 0.3s ease'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#fd5b38';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                              }}
                            />
                          </div>
                        );
                      }).filter(Boolean); // Remove null values
                    })()}
                  </div>
                );
              } else if (input.type === 'sliders') {
                return (
                  <div key={inputIndex} className="sliders-group">
                    {input.label && (
                      <div>
                        <label style={{
                          display: 'block',
                          marginBottom: '5px',
                          color: '#fff',
                          fontSize: '16px',
                          fontWeight: '500'
                        }}>{input.label}</label>
                        {input.subLabel && (
                          <label style={{
                            display: 'block',
                            marginBottom: '15px',
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontSize: '14px'
                          }}>{input.subLabel}</label>
                        )}
                      </div>
                    )}
                    {input.sliders.map((slider, sliderIndex) => {
                      const labels = slider.label.split(' ←→ ');
                      const isThreeLabels = labels.length === 3;
                      return (
                        <div key={sliderIndex} className="slider-item" style={{
                          marginBottom: '20px'
                        }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginBottom: '10px',
                            position: 'relative'
                          }}>
                            <span style={{
                              color: '#fff',
                              fontSize: '14px'
                            }}>{labels[0]}</span>
                            {isThreeLabels && (
                              <span style={{
                                color: '#fff',
                                fontSize: '14px',
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)'
                              }}>{labels[1]}</span>
                            )}
                            <span style={{
                              color: '#fff',
                              fontSize: '14px'
                            }}>{isThreeLabels ? labels[2] : labels[1]}</span>
                          </div>
                          <input
                            type="range"
                            min={slider.min}
                            max={slider.max}
                            value={sliderValues[index]?.[sliderIndex] || 50}
                            onChange={(e) => {
                              handleSliderChange(index, sliderIndex, e.target.value);
                              onModify?.();
                            }}
                            className="slider"
                            style={{
                              width: '100%',
                              height: '4px',
                              WebkitAppearance: 'none',
                              background: 'rgba(255, 255, 255, 0.1)',
                              borderRadius: '2px',
                              outline: 'none'
                            }}
                          />
                          <style>
                            {`
                              .slider::-webkit-slider-thumb {
                                -webkit-appearance: none;
                                appearance: none;
                                width: 20px;
                                height: 20px;
                                border-radius: 50%;
                                background: #fd5b38;
                                cursor: pointer;
                                border: none;
                              }
                              .slider::-moz-range-thumb {
                                width: 20px;
                                height: 20px;
                                border-radius: 50%;
                                background: #fd5b38;
                                cursor: pointer;
                                border: none;
                              }
                            `}
                          </style>
                        </div>
                      );
                    })}
                  </div>
                );
              } else if (input.type === 'text') {
                // Skip conditional text inputs that are already rendered with their parent radio inputs
                if (input.conditionalOn) {
                  // Find the radio input that is associated with this text input
                  let parentRadioInput = null;
                  
                  // Look backwards from this text input to find radio inputs that match
                  for (let i = inputIndex - 1; i >= 0; i--) {
                    const prevInput = slide.inputs[i];
                    if (prevInput.type === 'radio' && prevInput.options.some(opt => {
                      const optionKey = typeof opt === 'string' ? opt : (opt?.key || opt?.mainText);
                      return optionKey === input.conditionalOn;
                    })) {
                      parentRadioInput = prevInput;
                      break; // Found the most recent radio input that matches
                    }
                  }
                  
                  if (parentRadioInput) {
                    // Check if this radio input is single-select or multi-select
                    const parentRadioIndex = slide.inputs.indexOf(parentRadioInput);
                    const isParentMultiSelect = parentRadioInput.maxSelections > 1;
                    
                    if (isParentMultiSelect) {
                      // For multi-select radio, only skip if this text input comes immediately after the radio
                      if (inputIndex === parentRadioIndex + 1) {
                        return null; // This text input is immediately after its parent radio
                      }
                    } else {
                      // For single-select radio, always skip since it searches the entire slide
                      return null;
                    }
                  }
                }
                
                // Special handling for slide 5 (One of One) - one-to-one mapping
                if (index === 4) {
                  const currentRadioSelection = radioSelections[`${index}-0`];
                  if (currentRadioSelection && input.conditionalOn === currentRadioSelection) {
                    return (
                      <div key={inputIndex} className="text-input" style={{
                        marginBottom: '30px'
                      }}>
                        <label style={{
                          display: 'block',
                          marginBottom: '8px',
                          color: '#fff',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}>{input.label}</label>
                        <input
                          type="text"
                          value={promptValues[`${index}-${inputIndex}`] || ''}
                          onChange={(e) => {
                            handleInputChange(index, inputIndex, e.target.value);
                            onModify?.();
                          }}
                          placeholder={input.placeholder}
                          className="form-control"
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            color: '#fff',
                            fontSize: '14px',
                            outline: 'none',
                            transition: 'all 0.3s ease'
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = '#fd5b38';
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                          }}
                        />
                      </div>
                    );
                  }
                  return null;
                }
                
                return (
                  <div key={inputIndex} className="text-input" style={{
                    marginBottom: index === 4 ? '30px' : '0'
                  }}>
                    <label>{input.label}</label>
                    <input
                      type="text"
                      value={promptValues[`${index}-${inputIndex}`] || ''}
                      onChange={(e) => {
                        console.log('Text input changed:', {
                          index,
                          inputIndex,
                          value: e.target.value,
                          currentPromptValues: promptValues
                        });
                        setHasModifiedText(true);
                        handleInputChange(index, inputIndex, e.target.value);
                        onModify?.();
                      }}
                      placeholder={input.placeholder}
                      className="form-control"
                    />
                  </div>
                );
              }
              return null;
            })}
          </div>
        );

      case 'multiple-text':
        return (
          <div className="multiple-text-inputs">
            {slide.inputs.map((input, inputIndex) => (
              <div key={inputIndex} className="text-input">
                <label>{input.label}</label>
                <input
                  type="text"
                  value={multipleTextValues[index]?.[inputIndex] || ''}
                  onChange={(e) => {
                    handleMultipleTextChange(index, inputIndex, e.target.value);
                    onModify?.();
                  }}
                  placeholder={input.placeholder}
                  className="form-control"
                />
              </div>
            ))}
          </div>
        );

      case 'image-upload':
        return (
          <div className="image-upload">
            <label style={{
              display: 'block',
              marginBottom: '15px',
              color: '#fff',
              fontSize: '16px',
              fontWeight: '500'
            }}>{slide.description}</label>
            <div className="upload-area" style={{
              position: 'relative',
              width: '100%',
              minHeight: '200px',
              border: `2px dashed ${uploadWarning ? '#ff4444' : 'rgba(255, 255, 255, 0.2)'}`,
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: uploadWarning ? 'rgba(255, 68, 68, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              padding: '0',
              animation: uploadWarning ? 'shake 0.5s ease-in-out' : 'none',
              ':hover': {
                borderColor: '#fd5b38',
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDrop={handleDrop}
            onClick={() => {
              // Find the file input element and trigger a click
              const fileInput = document.querySelector('.file-input');
              if (fileInput) {
                fileInput.click();
              }
            }}
            >
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => {
                  handleImageUpload(e);
                  onModify?.();
                }}
                className="file-input"
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  top: 0,
                  left: 0,
                  opacity: 0,
                  cursor: 'pointer',
                  fontSize: '0'
                }}
              />
              {uploadedImages.length === 0 && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  textAlign: 'center',
                  color: uploadWarning ? '#ff4444' : '#fff',
                  fontSize: '14px',
                  pointerEvents: 'none',
                  width: '100%',
                  transition: 'all 0.3s ease'
                }}>
                  {uploadWarning ? 'Maximum of 5 images allowed' : 'Click or drag images here'}
                </div>
              )}
              <style>
                {`
                  @keyframes shake {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-5px); }
                    75% { transform: translateX(5px); }
                  }
                `}
              </style>
              <div className="uploaded-images" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                gap: '10px',
                padding: '10px',
                width: '100%'
              }}>
                {uploadedImages.map((image) => (
                  <div key={image.id} className="image-preview" style={{
                    position: 'relative',
                    aspectRatio: '1',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }}>
                    <img 
                      src={image.url.startsWith('blob:') ? null : image.url} 
                      alt={`Uploaded ${image.id}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                      onLoad={(e) => {
                        console.log('DEBUG IMAGE LOAD:', { 
                          imageUrl: image.url,
                          isGcs: image.url.startsWith('gs://'),
                          isBlob: image.url.startsWith('blob:'),
                          isHttp: image.url.startsWith('http')
                        });
                        if (image.url.startsWith('gs://')) {
                          getFreshSignedUrl(image.url).then(signedUrl => {
                            if (signedUrl) {
                              e.target.src = signedUrl;
                            }
                          });
                        }
                      }}
                      onError={(e) => {
                        console.log('DEBUG IMAGE ERROR:', { 
                          imageUrl: image.url,
                          isGcs: image.url.startsWith('gs://'),
                          isBlob: image.url.startsWith('blob:'),
                          isHttp: image.url.startsWith('http')
                        });
                        if (image.url.startsWith('gs://')) {
                          getFreshSignedUrl(image.url).then(signedUrl => {
                            if (signedUrl) {
                              e.target.src = signedUrl;
                            }
                          });
                        }
                      }}
                    />
                    {loadingImages[image.id] && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '14px'
                      }}>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            border: '2px solid #fff',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          <span>Uploading...</span>
                        </div>
                      </div>
                    )}
                    {failedImages[image.id] && (
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(255, 68, 68, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: '14px',
                        textAlign: 'center',
                        padding: '10px'
                      }}>
                        Upload Failed
                      </div>
                    )}
                    <button
                      onClick={() => removeImage(image.id)}
                      className="remove-image"
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '16px',
                        transition: 'all 0.3s ease',
                        zIndex: 2
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };
  
  // Function to get a fresh signed URL for a brand image
  const getFreshSignedUrl = async (imageUrl) => {
    console.log('DEBUG IMAGE URL:', { 
      imageUrl,
      isGcs: imageUrl?.startsWith('gs://'),
      isBlob: imageUrl?.startsWith('blob:'),
      isHttp: imageUrl?.startsWith('http')
    });
    
    try {
      if (!imageUrl) {
        console.log('DEBUG: No image URL provided');
        return null;
      }
      
      // If the URL already starts with gs://, use it directly
      if (imageUrl.startsWith('gs://')) {
        console.log('DEBUG: URL is already in gs:// format, using directly:', imageUrl);
        const signedUrl = await getSignedUrl(imageUrl);
        console.log('DEBUG: Received signed URL:', signedUrl);
        return signedUrl;
      }
      
      // Extract the path from the URL
      let path = '';
      if (imageUrl.includes('storage.googleapis.com/') || imageUrl.includes('storage.cloud.google.com/')) {
        // Remove query parameters
        const urlWithoutQuery = imageUrl.split('?')[0];
        // Get the path after the domain
        const pathMatch = urlWithoutQuery.match(/\/(caspian-\d+\/images\/[^/]+)/);
        if (pathMatch) {
          path = pathMatch[1];
          console.log('DEBUG: Extracted path:', path);
        }
      }
      
      if (!path) {
        console.log('DEBUG: Could not extract path from URL');
        return null;
      }
      
      // Build the GCS URL
      const gsUrl = `gs://${path}`;
      console.log('DEBUG: Built GCS URL:', gsUrl);
      
      // Get the signed URL
      const signedUrl = await getSignedUrl(gsUrl);
      console.log('DEBUG: Received signed URL:', signedUrl);
      
      return signedUrl;
    } catch (error) {
      console.error('DEBUG: Error getting signed URL:', error);
      return null;
    }
  };

  // Update the effect to monitor project changes and preload images
  useEffect(() => {
    if (project?.brands?.length > 0) {
      // Sort brands by generatedAt in descending order
      const sortedBrands = [...project.brands].sort((a, b) => 
        new Date(b.generatedAt) - new Date(a.generatedAt)
      );
      
      // Get the latest brand
      const latestBrand = sortedBrands[0];
      
      // Find the index of the latest brand in the original project.brands array
      const latestBrandIndex = project.brands.findIndex(brand => brand.generatedAt === latestBrand.generatedAt);
      
      // Synchronize selectedBrandIndex with the latest brand
      if (latestBrandIndex !== -1) {
        setSelectedBrandIndex(latestBrandIndex);
      }
      
      console.log('Project brand assets updated:', {
        imageUrl: latestBrand.imageUrl,
        tagline: latestBrand.tagline,
        selectedBrandIndex: latestBrandIndex
      });
      
      // Preload the image
      const img = new Image();
      img.onload = () => {
        console.log('New image loaded successfully');
        setPreloadedImageUrl("https://www.w3.org/WAI/content-images/tutorials/images/w3c.png" || latestBrand.imageUrl);
        setIsImageLoaded(true);
        
        // Update UI state after image is loaded
        setShowExampleSlide(true);
        setHelloTextState('hidden');
        setShowWhiteDiv(false);
        setIsAnimatingOut(true);
        setRightSideExpanded(true);
      };
      img.onerror = async (error) => {
        console.error('Failed to load image:', error);
        // Try to get a fresh signed URL
        const freshUrl = "https://www.w3.org/WAI/content-images/tutorials/images/w3c.png" || await getFreshSignedUrl(latestBrand.imageUrl);
        if (freshUrl) {
          // Try loading the image again with the fresh URL
          const newImg = new Image();
          newImg.onload = () => {
            console.log('Image loaded successfully with fresh URL');
            setPreloadedImageUrl(freshUrl);
            setIsImageLoaded(true);
            
            // Update UI state after image is loaded
            setShowExampleSlide(true);
            setHelloTextState('hidden');
            setShowWhiteDiv(false);
            setIsAnimatingOut(true);
            setRightSideExpanded(true);
          };
          newImg.onerror = () => {
            console.error('Failed to load image even with fresh URL');
            setIsImageLoaded(false);
          };
          newImg.src = freshUrl;
        } else {
          setIsImageLoaded(false);
        }
      };
      img.src = "https://www.w3.org/WAI/content-images/tutorials/images/w3c.png" || latestBrand.imageUrl;
    } else {
      console.log('No image URL in project:', {
        hasProject: !!project,
        hasBrands: !!project?.brands,
        brandsLength: project?.brands?.length,
        firstBrand: project?.brands?.[0]
      });
    }
  }, [project?.brands]);
  
  // Add effect to handle initial project data
  useEffect(() => {
    const initializeProject = async () => {
      console.log('INITIAL PROJECT EFFECT TRIGGERED', { initialProject });
      if (initialProject) {
        console.log('INITIAL PROJECT EXISTS', {
          projectId: initialProject.id,
          hasImages: !!initialProject.images,
          imageCount: initialProject.images?.length
        });

        setProject(initialProject);
        if (startOnLastSlide) {
          setTimeout(() => {
            setFinalVideo('/assets/video/vid16.mp4');
            setIsLastSubmitted(true);
            setShowExampleSlide(true);
            setHelloTextState('hidden');
            setSliderFullWidth(false);
          }, 1500);
        } else {
          setActiveIndex(0);
          setShowExampleSlide(false);
          setGenerateClicked(false);
          setIsLastSubmitted(false);
          setFinalVideo('');
          setShowContent(true);
        }
      }
    };

    initializeProject();
  }, [initialProject, startOnLastSlide]);

  // Add this useEffect after the other useEffect hooks
  useEffect(() => {
    const initializeProjectSettings = async () => {
      if (project?.settings) {
        console.log('Full project data received:', project);
        console.log('Project settings:', project.settings);
        
        const { settings } = project;
        
        // Prefill prompt values
        if (settings.promptValues) {
          console.log('Found prompt values:', settings.promptValues);
          const newPromptValues = {};
          settings.promptValues.forEach(({ slideIndex, inputIndex, value }) => {
            newPromptValues[`${slideIndex}-${inputIndex}`] = value;
          });
          console.log('Setting prompt values to:', newPromptValues);
          setPromptValues(newPromptValues);
        }
        
        // Prefill checkbox selections
        if (settings.checkboxSelections) {
          console.log('Found checkbox selections:', settings.checkboxSelections);
          setCheckboxSelections(settings.checkboxSelections);
        }
        
        // Prefill radio selections
        if (settings.radioSelections) {
          console.log('Found radio selections:', settings.radioSelections);
          setRadioSelections(settings.radioSelections);
        }
        
        // Prefill multi-select radio selections
        if (settings.multiSelectRadioSelections) {
          console.log('Found multi-select radio selections:', settings.multiSelectRadioSelections);
          setMultiSelectRadioSelections(settings.multiSelectRadioSelections);
        }
        
        // Prefill slider values
        if (settings.sliderValues) {
          console.log('Found slider values:', settings.sliderValues);
          setSliderValues(settings.sliderValues);
        }
        
        // Prefill multiple text values
        if (settings.multipleTextValues) {
          console.log('Found multiple text values:', settings.multipleTextValues);
          setMultipleTextValues(settings.multipleTextValues);
        }
        
        // If there are uploaded images, set them
        if (project.images) {
          console.log('Found project images:', project.images);
          const newUploadedImages = await Promise.all(project.images.map(async img => {
            console.log('Processing image URL before presigning:', img.url);
            const presignedUrl = await getFreshSignedUrl(img.url);
            console.log('Generated presigned URL:', presignedUrl);
            return {
              url: presignedUrl || img.url,
              gcsUrl: img.url,
              id: Math.random().toString(36).substr(2, 9)
            };
          }));
          console.log('Setting uploaded images to:', newUploadedImages);
          setUploadedImages(newUploadedImages);
        }

        // Set the active index to the last slide if we're starting on the last slide
        if (startOnLastSlide) {
          console.log('Setting active index to last slide:', data.length - 1);
          setActiveIndex(data.length - 1);
        }

        // Log the current state of all input fields
        console.log('Current state of all input fields:', {
          promptValues,
          checkboxSelections,
          radioSelections,
          multiSelectRadioSelections,
          sliderValues,
          multipleTextValues,
          uploadedImages
        });
      }
    };

    initializeProjectSettings();
  }, [project, startOnLastSlide]);

  // Update the getItemWidth function to handle transitions better
  const getItemWidth = (index, isLastSubmitted, isActive) => {
    if (generateClicked) {
      return {transition: 'all 1.5s cubic-bezier(0.645, 0.045, 0.355, 1)'};
    }
    
    if (showExampleSlide) {
      return {width: '0', opacity: 0, visibility: 'hidden'};
    }
    
    return {
      width: isActive ? '100%' : '0',
      opacity: isActive ? 1 : 0,
      visibility: isActive ? 'visible' : 'hidden',
      transition: 'all 1.5s cubic-bezier(0.645, 0.045, 0.355, 1)'
    };
  };

  // Add handleRetryGeneration function
  const handleRetryGeneration = async (refresh = false) => {
    // Check if we've reached the maximum number of brands (4)
    if (project.brands.length >= 4) {
      setRetryError('Maximum number of brand variations (4) reached');
      setRetryErrorAnimation(true);
      setTimeout(() => {
        setRetryErrorAnimation(false);
      }, 500);
      setTimeout(() => {
        setRetryError(null);
      }, 4000);
      return;
    }

    try {
      console.log('Starting brand generation retry...');
      
      // Reset to loading state and show default background
      setIsImageLoaded(false);
      setPreloadedImageUrl('/assets/images/pages/authentication/checkout.png');
      setIsInitialGeneration(true); // Set to true for new generation
      setIsBrandSwitch(true); // Add this to show glass loading screen
      
      // Skip video animation and go directly to example slide
      setShowExampleSlide(true);
      setHelloTextState('hidden');
      setShowWhiteDiv(false);
      setIsAnimatingOut(true);
      setRightSideExpanded(true);

      // Create updated project data with new form values
      const updatedProjectData = {
        title: promptValues['0-0'] || project.title,
        description: promptValues['1-0'] || project.description,
        images: uploadedImages.map(img => ({
          url: img.gcsUrl,
          previewUrl: img.url,
          uploadedAt: new Date().toISOString()
        })),
        settings: {
          checkboxSelections: Object.entries(checkboxSelections).reduce((acc, [slideIndex, selections]) => ({
            ...acc,
            [slideIndex]: selections
          }), {}),
          radioSelections: Object.entries(radioSelections).reduce((acc, [slideIndex, selection]) => ({
            ...acc,
            [slideIndex]: selection
          }), {}),
          multiSelectRadioSelections: multiSelectRadioSelections,
          sliderValues: Object.entries(sliderValues).reduce((acc, [slideIndex, values]) => ({
            ...acc,
            [slideIndex]: values
          }), {}),
          multipleTextValues: Object.entries(multipleTextValues).reduce((acc, [slideIndex, values]) => ({
            ...acc,
            [slideIndex]: values
          }), {}),
          promptValues: Object.entries(promptValues).map(([key, value]) => {
            const [slideIndex, inputIndex] = key.split('-');
            return {
              slideIndex: parseInt(slideIndex),
              inputIndex: parseInt(inputIndex),
              value: value,
              question: data[parseInt(slideIndex)]?.question,
              type: data[parseInt(slideIndex)]?.type
            };
          })
        },
        // Keep existing brands array
        brands: project.brands
      };
      
      // Generate overview first
      const overview = await generateOverview();
      console.log('Generated overview:', overview);
      
      // Generate all brand elements in parallel
      const [
        brandManifesto,
        positioningStatement,
        audienceMatrix,
        toneMoodProfile,
        visualVibeSummary,
        tagline,
        image
      ] = await Promise.all([
        generateBrandManifesto(overview),
        generatePositioningStatement(overview),
        generateAudienceMatrix(overview),
        generateToneMoodProfile(overview),
        generateVisualVibeSummary(overview),
        generateTagline(overview),
        generateImage(overview)
      ]);
      
      // Add new brand to the project
      const newBrand = {
        imageUrl: image,
        tagline,
        brandManifesto,
        positioningStatement,
        audienceMatrix,
        toneMoodProfile,
        visualVibeSummary,
        generatedAt: new Date().toISOString()
      };
      
      // Update project with new brand
      const updatedProject = await updateProject(project.id, {
        ...updatedProjectData,
        brands: [...project.brands, newBrand]
      });

      // Update local state with the new brand data
      setProject(updatedProject);
      
      // Set the new brand as the current one
      const newBrandIndex = updatedProject.brands.length - 1;
      setSelectedBrandIndex(newBrandIndex);
      
      // Get a fresh signed URL for the new image
      const freshUrl = await getFreshSignedUrl(image);
      setPreloadedImageUrl(freshUrl || image);
      
      // Create a new image element to preload the image
      const img = new Image();
      img.onload = () => {
        setIsImageLoaded(true);
        setIsBrandSwitch(false); // Remove glass overlay once image is loaded
        
        // Update the dropdown menu by triggering a change event
        const selectElement = document.querySelector('select');
        if (selectElement) {
          selectElement.value = newBrandIndex;
          // Create and dispatch a change event
          const event = new Event('change', { bubbles: true });
          selectElement.dispatchEvent(event);
        }
      };
      img.onerror = async () => {
        console.error('Failed to load brand image:', image);
        // Try one more time with the original URL if fresh URL fails
        const img2 = new Image();
        img2.onload = () => {
          setPreloadedImageUrl(image);
          setIsImageLoaded(true);
          setIsBrandSwitch(false);
          
          // Update the dropdown menu by triggering a change event
          const selectElement = document.querySelector('select');
          if (selectElement) {
            selectElement.value = newBrandIndex;
            // Create and dispatch a change event
            const event = new Event('change', { bubbles: true });
            selectElement.dispatchEvent(event);
          }
        };
        img2.onerror = () => {
          console.error('Failed to load brand image even with original URL');
          setIsImageLoaded(false);
          setIsBrandSwitch(false);
        };
        img2.src = image;
      };
      img.src = freshUrl || image;
      if(refresh) {
        // Refresh the page after creating the brand to ensure clean state
        console.log("refreshing page")
        console.log("refresh", refresh)
        setTimeout(() => {
          window.location.reload();
        }, 4000);
      }
    } catch (error) {
      console.error('Error in brand generation retry:', error);
      showToast('An error occurred while generating new brand assets');
      // Reset loading state on error
      setIsImageLoaded(true);
      setIsBrandSwitch(false); // Also remove glass overlay on error
      setPreloadedImageUrl(project.brands[0].imageUrl);
    }
  };

  // Add deleteFromBrand function
  const deleteFromBrand = async (index) => {
    console.log('DELETE FROM BRAND - Function called:', {
      index,
      currentBrandIndex: project.currentBrandIndex,
      selectedBrandIndex,
      allBrands: project.brands,
      brandToDelete: project.brands[index],
      currentPreloadedUrl: preloadedImageUrl
    });

    try {
      // Get the current brand index
      const currentBrandIndex = project.currentBrandIndex ?? 0;
      const brandToDelete = project.brands[index];
      
      console.log('DELETE FROM BRAND - Brand details:', {
        currentBrandIndex,
        brandToDelete,
        totalBrands: project.brands.length
      });
      
      // If this is the last brand, delete the entire project
      if (project.brands.length === 1) {
        console.log('DELETE FROM BRAND - Last brand, deleting entire project');
        try {
          await deleteProject(project.id);
          navigate('/brands/');
          return;
        } catch (error) {
          console.error('DELETE FROM BRAND - Error deleting project:', error);
          showToast('Failed to delete project. Please try again.');
          return;
        }
      }
      
      // Filter out the brand to delete
      const updatedBrands = project.brands.filter((_, i) => i !== index);
      
      console.log('DELETE FROM BRAND - Updated brands array:', {
        originalLength: project.brands.length,
        newLength: updatedBrands.length,
        removedIndex: index,
        updatedBrands
      });
      
      // Update the project in Firestore
      const updateData = {
        brands: updatedBrands
      };
      
      console.log('DELETE FROM BRAND - Updating Firestore:', {
        projectId: project.id,
        updateData
      });
      
      const updatedProject = await updateProject(project.id, updateData);
      console.log('DELETE FROM BRAND - Project updated successfully:', {
        updatedProject,
        brands: updatedProject.brands
      });
      
      // Update the project state
      setProject(updatedProject);
      
      // If we deleted the current brand, show the last remaining brand
      if (updatedBrands.length > 0) {
        setSelectedBrandIndex(updatedBrands.length - 1);
      } else {
        // If no brands left, go back to first slide
        setActiveIndex(0);
      }
      
      // Close the delete confirmation
      setShowDeleteConfirm(false);
      
    } catch (error) {
      console.error('DELETE FROM BRAND - Error:', error);
      showToast('Failed to delete brand. Please try again.');
    }
  };

  // Add effect to handle content visibility
  useEffect(() => {
    let timeoutId;
    if (activeIndex !== null) {
      setIsExpanding(true);
      setShowContent(false);
      
      timeoutId = setTimeout(() => {
        setShowContent(true);
        setIsExpanding(false);
      }, 800);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [activeIndex]);

  // Add effect to handle example slide transitions
  useEffect(() => {
    let timeoutId;
    if (!showExampleSlide) {
      // Reset states when example slide is hidden
      setHelloTextState('hidden');
      setShowWhiteDiv(false);
      setIsAnimatingOut(false);
      setRightSideExpanded(false);
      
      // Add a small delay before showing content
      timeoutId = setTimeout(() => {
        setShowContent(true);
      }, 100);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showExampleSlide]);

  // Add this after the state declarations and before other useEffect hooks
  useEffect(() => {
    // Check authentication state on component mount
    const currentUser = auth.currentUser;
    const storedAuth = localStorage.getItem('isAuthenticated') === 'true';
    const isAuthenticated = currentUser || storedAuth;

    if (isAuthenticated) {
      // If authenticated, immediately set these states to prevent auth panel from showing
      setHelloTextState('hidden');
      setShowWhiteDiv(false);
      setIsAnimatingOut(true);
      setRightSideExpanded(true);
    }
  }, []); // Run only on mount

  const brandStory = radioSelections['0-0'] || ''; // "What is your brand story?"
  const brandName = promptValues['0-3'] || ''; // "Enter your brand name"
  const brandNameBuild = promptValues['0-4'] || ''; // "Help be build a brand name. Here is my idea:"
  const brandNameOption = radioSelections['0-1'] || ''; // if "Skip for now", then Brand Name skip
  const brandGoals = multiSelectRadioSelections['0-2']?.join(', ') || ''; // "What are your main goals?"
  const brandEnergy = checkboxSelections[1]?.join(', ') || ''; // "Choose your brand's energy"
  const seriousVsPlayful = Number(sliderValues[1]?.[0]) || 50; // "Raw/Authentic ←→ Polished/Refined"
  const polishedVsRaw = Number(sliderValues[1]?.[1]) || 50; // "Traditional ←→ Innovative"
  const calmVsEnergetic = Number(sliderValues[1]?.[2]) || 50; // "Simple ←→ Complex"

  // Audience
  const idealCustomer = multiSelectRadioSelections['2-0']?.join(', ') || ''; // "Your People"
  const idealCustomerOther = promptValues['2-1'] || ''; // "Other" being idealCustomerOther
  const customerChallenge = multiSelectRadioSelections['2-2']?.join(', ') || '';
  const customerChallengeOther = promptValues['2-3'] || ''; // "Other" being customerChallengeOther
  const customerTransformation = promptValues['2-4'] || ''; // "After"

  // Which best captures your visual vibe?
  const visualVibe = radioSelections['3-0'] || ''; // "Which best captures your visual vibe?"
  const rawVsPolished = Number(sliderValues[3]?.[0]) || 50; // "Raw ←→ Polished"
  const traditionalVsInnovative = Number(sliderValues[3]?.[1]) || 50; // "Traditional ←→ Innovative"
  const simpleVsComplex = Number(sliderValues[3]?.[2]) || 50; // "Simple ←→ Complex"

  // Every brand has that one thing nobody else has. whats your?
  const whatMakesMeDifferent = promptValues['4-1'] || ''; // "What makes me different is"
  const weAreTheGoTo = promptValues['4-2'] || ''; // "We are the go-to for"
  const iStandOut = promptValues['4-3'] || ''; // "As an athlete, I stand out because"
  const iCreateFor = promptValues['4-4'] || ''; // "I create for"
  const peopleComeToMe = promptValues['4-5'] || ''; // "People come to me for"
  const writeMyOwn = promptValues['4-6'] || ''; // "Write my own"
  const lowKeyVsBoldTone = Number(sliderValues[4]?.[0]) || 50; // "Low Key ←→ Bold"
  const strategicVsDisruptiveTone = Number(sliderValues[4]?.[1]) || 50; // "Strategic ←→ Disruptive"
  
  const audience = `${idealCustomer} ${idealCustomerOther ? `(Other: ${idealCustomerOther})` : ''}`;
  const challenge = `${customerChallenge} ${customerChallengeOther ? `(Other: ${customerChallengeOther})` : ''}`;
  const uniquenessArray = [
    whatMakesMeDifferent ? `What makes me different is ${whatMakesMeDifferent}` : undefined, 
    weAreTheGoTo ? `We are the go-to for ${weAreTheGoTo}` : undefined, 
    iStandOut ? `As an athlete, I stand out because ${iStandOut}` : undefined, 
    iCreateFor ? `I create for ${iCreateFor}` : undefined, 
    peopleComeToMe ? `People come to me for ${peopleComeToMe}` : undefined, 
    writeMyOwn ? `${writeMyOwn}` : undefined
  ];
  const uniqueness = uniquenessArray.find(item =>  typeof item === 'string') || '';

  function scaleChoice(value, options) {
    const index = Math.floor((value / 100) * (options.length - 1));
    return options[index];
  }
  const rawVsPolishedChoice = scaleChoice(rawVsPolished, [
    "extremely raw and authentic",
    "balanced but slightly raw",
    "balanced",
    "balanced but slightly polished",
    "very polished and professional"
  ]);
  const traditionalVsInnovativeChoice = scaleChoice(traditionalVsInnovative, [
    "deeply rooted in tradition",
    "time-tested",
    "balanced",
    "modern and forward-thinking",
    "highly innovative and visionary"
  ]);
  const simpleVsComplexChoice = scaleChoice(simpleVsComplex, [
    "very clear and simple",
    "clear and straightforward",
    "balanced",
    "detailed and descriptive",
    "rich in detail and depth"
  ]);

  // Add these functions before handleRetryGeneration
  const generateOverview = async () => {

    const overviewPrompt = `Create a concise brand overview (2-3 lines) that captures the essence of this brand:
    Brand Name: ${brandName}
    Story: ${brandStory}
    Purpose: ${brandGoals}
    Target Audience: ${audience}
    Their Challenge: ${challenge}
    Their Transformation: ${customerTransformation}

    IMPORTANT: Start directly with the overview. Do not include any introductory text, explanations, or pleasantries. Focus on how the brand's purpose addresses the audience's challenge and leads to their transformation.`;

    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: overviewPrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Brand overview response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Brand overview generation failed';
  };

  const generateTagline = async (title) => {
    // Inject the overview into the tagline prompt
    const taglinePrompt = `The brand is ${title}\nCreate only one tagline for this company, in less than 20 words. Don't include any formatting or brand names.`;
    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: taglinePrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Tagline generation response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Brand tagline generation failed';
  };

  const generateBrandManifesto = async (title) => {

    // Create a manifesto prompt that captures the brand's essence
    const taglinePrompt = `Create a powerful brand manifesto for "${title}" that captures its essence and purpose. Consider:

    Our Purpose: ${brandGoals}
    Our Energy: ${brandEnergy}
    Our People: ${audience}
    Their Challenge: ${challenge}
    Their Transformation: ${customerTransformation}
    Our Uniqueness: ${uniqueness}

    The manifesto should be inspiring, authentic, and reflect our core values. It should speak directly to our audience while maintaining our ${visualVibe} aesthetic and ${brandEnergy} energy. Write it in a way that feels ${rawVsPolished > 50 ? 'polished and refined' : 'raw and authentic'}, ${traditionalVsInnovative > 50 ? 'innovative and forward-thinking' : 'grounded in tradition'}, and ${simpleVsComplex > 50 ? 'rich and layered' : 'clear and straightforward'}.

    Format it as a powerful declaration that our audience can rally behind.

    IMPORTANT: Start directly with the manifesto. Do not include any introductory text, explanations, or pleasantries. Begin immediately with the manifesto. Straight to the information. Do not say "Here you go" or anything like that.`;

    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: taglinePrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Tagline generation response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Brand tagline generation failed';
  };

  const generatePositioningStatement = async (title) => {

    const referenceImages = uploadedImages.map(img => img.gcsUrl).join(', ') || ''; // "Upload reference images"

    // Create a positioning statement prompt that focuses on market position and value proposition
    const taglinePrompt = `Create a clear and compelling positioning statement for "${title}" that defines its unique place in the market. Consider:

    Target Audience: ${audience}
    Their Current Challenge: ${challenge}
    Their Desired Transformation: ${customerTransformation}
    Our Unique Value: ${uniqueness}
    Our Market Position: ${brandStory}
    Our Purpose: ${brandGoals}

    The positioning statement should be concise and strategic, clearly articulating:
    1. Who we serve (${audience})
    2. What problem we solve (${challenge})
    3. How we solve it uniquely (${uniqueness})
    4. The transformation we deliver (${customerTransformation})

    Write it in a ${lowKeyVsBoldTone > 50 ? 'bold' : 'low key'} and ${strategicVsDisruptiveTone > 50 ? 'disruptive' : 'strategic'} tone that reflects our ${brandEnergy} energy and ${visualVibe} aesthetic. The statement should be ${simpleVsComplex > 50 ? 'rich in detail' : 'clear and straightforward'} while maintaining a ${traditionalVsInnovative > 50 ? 'forward-thinking' : 'time-tested'} approach.

    Format it as a clear, strategic statement that could be used in marketing materials and business planning.

    IMPORTANT: Start directly with the positioning. Do not include any introductory text, explanations, or pleasantries. Begin immediately with the positioning. Straight to the information. Do not say "Here you go" or anything like that.`;

    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: taglinePrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Positioning statement generation response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Positioning statement generation failed';
  };

  const generateAudienceMatrix = async (title) => {

    // Create an audience matrix prompt that defines three distinct audience segments
    const taglinePrompt = `Create a detailed audience matrix for "${title}" that defines three distinct audience segments. Consider our brand's core elements:

    Brand Purpose: ${brandStory}
    Brand Goals: ${brandGoals}
    Brand Energy: ${brandEnergy}
    Ideal Customer: ${audience}
    Customer Challenge: ${challenge}
    Customer Transformation: ${customerTransformation}
    Unique Value: ${uniqueness}

    Define three distinct audience segments:

    1. THE CREW
    - These are the people who will immediately understand and connect with our brand
    - They share our values and vision
    - They're already looking for what we offer
    - They're our early adopters and brand champions

    2. THE SKEPTICS
    - These are the people who won't initially get our brand
    - They might be resistant to our approach or values
    - They might prefer traditional alternatives
    - They're unlikely to convert in the short term

    3. THE CONVERTS
    - These are people who won't get it at first but can be won over
    - They're open to new ideas but need more convincing
    - They might be on the fence about our approach
    - They could become strong advocates with the right engagement

    For each segment, describe:
    - Their key characteristics
    - Their current mindset
    - What might attract or repel them
    - How they might interact with our brand

    Write in a ${rawVsPolished > 50 ? 'polished and professional' : 'raw and authentic'} tone that matches our ${brandEnergy} energy. The descriptions should be ${simpleVsComplex > 50 ? 'rich in detail' : 'clear and straightforward'} while maintaining a ${traditionalVsInnovative > 50 ? 'forward-thinking' : 'time-tested'} perspective.

    Format the response with clear sections for each audience segment, making it easy to understand the differences between them.

    IMPORTANT: Start directly with the audience segments. Do not include any introductory text, explanations, or pleasantries. Begin immediately with "1. THE CREW" and proceed with the content. Straight to the information. Do not say "Here you go" or anything like that.`;

    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: taglinePrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Audience matrix generation response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Audience matrix generation failed';
  };

  const generateToneMoodProfile = async (title) => {
    // Create a tone and mood profile prompt that includes color suggestions
    const taglinePrompt = `Create a detailed tone and mood profile for "${title}" based on these brand elements:

    Brand Energy: ${brandEnergy}
    Visual Vibe: ${visualVibe}
    Raw vs Polished: ${rawVsPolishedChoice}
    Traditional vs Innovative: ${traditionalVsInnovativeChoice}
    Simple vs Complex: ${simpleVsComplexChoice}

    Focus on the emotional resonance and personality of the brand.
    
    Include:
    1. Primary Tone: The dominant emotional quality that defines the brand's voice
    2. Communication Style: How the brand expresses itself
    3. Emotional Triggers: What feelings the brand aims to evoke
    
    Format the response in a clear, narrative style with markdown formatting.
    Use **bold** for key terms and *italic* for emphasis.
    Structure it with clear sections and bullet points where appropriate.
    
    Make it specific and avoid generic descriptions.
    The tone should reflect the brand's unique position and values.

    IMPORTANT: Start directly with the tone and mood profile. Do not include any introductory text, explanations, or pleasantries. Begin immediately with the tone and mood profile. Straight to the information. Do not say "Here you go" or anything like that.`;

    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: taglinePrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Tone and mood profile generation response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Tone & Mood Profile generation failed';
  };

  const generateVisualVibeSummary = async (title) => {
    // Create a visual vibe summary prompt
    const taglinePrompt = `Create a concise visual vibe summary (1-2 lines) for "${title}" that captures its aesthetic essence. Consider:

    Brand Energy: ${brandEnergy}
    Visual Vibe: ${visualVibe}
    Raw vs Polished: ${rawVsPolishedChoice}
    Traditional vs Innovative: ${traditionalVsInnovativeChoice}
    Simple vs Complex: ${simpleVsComplexChoice}

    The summary should:
    - Be 1-2 lines maximum
    - Describe the overall visual aesthetic
    - Capture the balance between raw/polished, traditional/innovative, and simple/complex
    - Reflect the brand's energy and vibe
    - Be descriptive but not prescriptive
    - Focus on the feeling and impression rather than specific design elements

    Format the response as a single string, no JSON or additional formatting.

    IMPORTANT: Start directly with the visual vibe content. Do not include any introductory text, explanations, or pleasantries. Begin immediately with the visual vibe content. Straight to the information. Do not say "Here you go" or anything like that.`;

    const taglineResponse = await fetch(`${API_URL}/generate-text`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: taglinePrompt
      }),
    }).catch(error => {
      console.warn('Failed to connect to text generation API:', error);
      return null;
    });
    if (taglineResponse && taglineResponse.ok) {
      const taglineData = await taglineResponse.json();
      console.log('Visual vibe summary generation response:', taglineData);
      return taglineData.result.trim();
    }
    return 'Visual vibe summary generation failed';
  };

  const generateImage = async (title) => {
    return "/assets/imgs/background.jpg";
    const referenceImages = uploadedImages.map(img => img.gcsUrl).join(', ') || ''; // "Upload reference images"

    // Extract answers for the three prompts
    const whoGetsIt = multipleTextValues[2]?.[0] || '';
    const beforeChallenge = multipleTextValues[2]?.[1] || '';
    const whatMakesYouUndeniable = promptValues[4] || '';
    console.log('Generating image for:', title, whoGetsIt, beforeChallenge, whatMakesYouUndeniable);
    // Call generateBrandTextOverview with these answers

    console.log('Generating brand grid image for:', title);
    const imagePrompt = `Do not include text. Create a grid of relevant images to be used in the background of a branding document for "${title}".`;
    
    const brandGridResponse = await fetch(`${API_URL}/generate-image`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify({
        prompt: imagePrompt,
        aspectRatio: '4:3'
      }),
    }).catch(error => {
      console.warn('Failed to connect to image generation API:', error);
      return null;
    });
    
    if (brandGridResponse && brandGridResponse.ok) {
      const brandGridData = await brandGridResponse.json();
      console.log('Image generation response:', brandGridData);
      return brandGridData.result;
    }
    
    return null;
  };

  // Add this function to close the overlay
  const handleCloseOverlay = () => {
    setShowOverlay(false);
    setActiveSection(null);
  };

  const handleRedirection = (index) => {
    // First hide the example slide and reset all states
    setShowExampleSlide(false);
    setGenerateClicked(false);
    setIsLastSubmitted(false);
    setFinalVideo('');
    setHelloTextState('hidden');
    setShowWhiteDiv(false);
    setIsAnimatingOut(false);
    setRightSideExpanded(false);
    setShowContent(false);

    // Use a small delay to ensure state updates are processed
    requestAnimationFrame(() => {
      setActiveIndex(index);
      requestAnimationFrame(() => {
        setShowContent(true);
      });
    });
  };

  // Add state for authentication popup
  const [authPopUp, setAuthPopUp] = useState(false);

  // Helper to check if user has been asked for survey
  async function hasBeenAskedForSurvey(userId) {
    console.log('hasBeenAskedForSurvey called with userId:', userId);
    
    if (!userId) {
      const localValue = localStorage.getItem('surveyAsked');
      console.log('No userId provided, checking localStorage:', localValue);
      return localValue === 'true';
    }
    
    try {
      console.log('Attempting to fetch user document for userId:', userId);
      const userDoc = await getDoc(doc(db, 'users', userId));
      console.log('User document exists:', userDoc.exists());
      console.log('User document data:', userDoc.data());
      return userDoc.exists() && userDoc.data().surveyAsked === true;
    } catch (error) {
      console.error('Error in hasBeenAskedForSurvey:', error);
      return false;
    }
  }
  async function markSurveyAsked(userId) {
    if (!userId) return localStorage.setItem('surveyAsked', 'true');
    await setDoc(doc(db, 'users', userId), { surveyAsked: true }, { merge: true });
  }

  // Add generatePDF function
  const generatePDF = async () => {
    if (!project?.brands?.[selectedBrandIndex]) {
      showToast('No brand data available to generate PDF');
      return;
    }

    const brand = project.brands[selectedBrandIndex];
    
    const renderMarkdownContent = (content) => {
      if (!content) return null;
      
      // Split content by double line breaks to handle paragraphs
      const paragraphs = content.split('\n\n');
      
      return paragraphs.map((paragraph, index) => {
        // Handle headings
        if (paragraph.trim().startsWith('# ')) {
          return (
            <Text key={index} style={[styles.sectionTitle, { fontSize: 24, marginBottom: 15 }]}>
              {paragraph.replace(/^# /, '')}
            </Text>
          );
        }
        if (paragraph.trim().startsWith('## ')) {
          return (
            <Text key={index} style={[styles.sectionTitle, { fontSize: 20, marginBottom: 12 }]}>
              {paragraph.replace(/^## /, '')}
            </Text>
          );
        }
        if (paragraph.trim().startsWith('### ')) {
          return (
            <Text key={index} style={[styles.sectionTitle, { fontSize: 16, marginBottom: 10 }]}>
              {paragraph.replace(/^### /, '')}
            </Text>
          );
        }
        
        // Handle bullet points
        if (paragraph.trim().startsWith('* ')) {
          const items = paragraph.split('\n').filter(item => item.trim().startsWith('* '));
          return (
            <View key={index} style={styles.paragraph}>
              {items.map((item, itemIndex) => {
                const content = item.replace(/^\*\s*/, '');
                return (
                  <Text key={itemIndex} style={styles.listItem}>
                    • {parseMarkdownText(content)}
                  </Text>
                );
              })}
            </View>
          );
        }
        
        // Handle regular paragraphs with markdown
        return (
          <Text key={index} style={styles.paragraph}>
            {parseMarkdownText(paragraph)}
          </Text>
        );
      });
    };

    try {
      const blob = await pdf(
        <Document>
          {/* Brand Manifesto Page */}
          <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
              <Text style={styles.headerLeft}>Narrative OS™ Brand Blueprint</Text>
            </View>

            <View style={[styles.section, {marginTop: 15}]}>
              <Text style={[{fontSize: 10, textAlign: 'center', color: "grey", marginBottom: 25, marginTop: 25}]}>{brand.tagline}</Text>
              <Text style={styles.sectionTitle}>Brand Manifesto</Text>
              <View style={styles.sectionContent}>
                {renderMarkdownContent(brand.brandManifesto)}
              </View>
            </View>

            <View style={styles.footer} fixed>
              <Text>Page 1 of 5</Text>
              <Text>© {new Date().getFullYear()} Narrative OS. All rights reserved.</Text>
            </View>
          </Page>

          {/* Positioning Statement Page */}
          <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
              <Text style={styles.headerLeft}>Narrative OS™ Brand Blueprint</Text>
            </View>

            <View style={[styles.section, {marginTop: 15}]}>
              <Text style={styles.sectionTitle}>Positioning Statement</Text>
              <View style={styles.sectionContent}>
                {renderMarkdownContent(brand.positioningStatement)}
              </View>
            </View>

            <View style={styles.footer} fixed>
              <Text>Page 2 of 5</Text>
              <Text>© {new Date().getFullYear()} Narrative OS. All rights reserved.</Text>
            </View>
          </Page>

          {/* Audience Matrix Page */}
          <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
              <Text style={styles.headerLeft}>Narrative OS™ Brand Blueprint</Text>
            </View>

            <View style={[styles.section, {marginTop: 15}]}>
              <Text style={styles.sectionTitle}>Audience Matrix</Text>
              <View style={styles.sectionContent}>
                {renderMarkdownContent(brand.audienceMatrix)}
              </View>
            </View>

            <View style={styles.footer} fixed>
              <Text>Page 3 of 5</Text>
              <Text>© {new Date().getFullYear()} Narrative OS. All rights reserved.</Text>
            </View>
          </Page>

          {/* Tone & Mood Profile Page */}
          <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
              <Text style={styles.headerLeft}>Narrative OS™ Brand Blueprint</Text>
            </View>

            <View style={[styles.section, {marginTop: 15}]}>
              <Text style={styles.sectionTitle}>Tone & Mood Profile</Text>
              <View style={styles.sectionContent}>
                {renderMarkdownContent(brand.toneMoodProfile)}
              </View>
            </View>

            <View style={styles.footer} fixed>
              <Text>Page 4 of 5</Text>
              <Text>© {new Date().getFullYear()} Narrative OS. All rights reserved.</Text>
            </View>
          </Page>

          {/* Visual Vibe Summary Page */}
          <Page size="A4" style={styles.page}>
            <View style={styles.header} fixed>
              <Text style={styles.headerLeft}>Narrative OS™ Brand Blueprint</Text>
            </View>

            <View style={[styles.section, {marginTop: 15}]}>
              <Text style={styles.sectionTitle}>Visual Vibe Summary</Text>
              <View style={styles.sectionContent}>
                {renderMarkdownContent(brand.visualVibeSummary)}
              </View>
            </View>

            <View style={styles.footer} fixed>
              <Text>Page 5 of 5</Text>
              <Text>© {new Date().getFullYear()} Narrative OS. All rights reserved.</Text>
            </View>
          </Page>
        </Document>
      ).toBlob();

      const filename = "brand-blueprint.pdf";
      fileDownload(blob, filename);

      // Track brand download
      trackBrandDownloaded(brand.id);

    } catch (error) {
      console.error('PDF generation failed:', error);
      showToast('Failed to generate PDF');
      trackError('pdf_generation_error', error.message);
    }
  };

  const parseMarkdownText = (text) => {
    if (!text) return null;

    // First handle bold text (double asterisks)
    const parts = text.split(/(\*\*.*?\*\*)/g);
    
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // This is a bold section
        const content = part.slice(2, -2);
        // Now handle any italic text within the bold section
        const boldParts = content.split(/(\*.*?\*)/g);
        return (
          <Text key={index} style={styles.strong}>
            {boldParts.map((boldPart, boldIndex) => {
              if (boldPart.startsWith('*') && boldPart.endsWith('*')) {
                return (
                  <Text key={boldIndex} style={[styles.strong, styles.em]}>
                    {boldPart.slice(1, -1).replace(/\*/g, '')}
                  </Text>
                );
              }
              return boldPart;
            })}
          </Text>
        );
      }
      
      // Handle italic text in non-bold sections
      const italicParts = part.split(/(\*.*?\*)/g);
      return (
        <Text key={index}>
          {italicParts.map((italicPart, italicIndex) => {
            if (italicPart.startsWith('*') && italicPart.endsWith('*')) {
              return (
                <Text key={italicIndex} style={styles.em}>
                  {italicPart.slice(1, -1).replace(/\*/g, '')}
                </Text>
              );
            }
            return italicPart.replace(/\*/g, '');
          })}
        </Text>
      );
    });
  };

  // Add onShare function
  const onShare = async () => {
    try {
      console.log('onShare called with project:', project);
      
      // Get the current brand
      const currentBrand = project.brands[selectedBrandIndex];
      
      // Ensure brand has an ID
      if (!currentBrand.id) {
        // Generate a unique ID for the brand
        const brandId = Math.random().toString(36).substring(2, 15);
        currentBrand.id = brandId;
        
        // Update the brand in the project
        const updatedBrands = [...project.brands];
        updatedBrands[selectedBrandIndex] = currentBrand;
        
        // Update project in Firestore with the new brand ID
        const updatedProject = await updateProject(project.id, {
          ...project,
          brands: updatedBrands
        });
        
        // Update local state
        setProject(updatedProject);
      }
      
      // Check if project and brand are already public
      if (project.access === 'public' && currentBrand.access === 'public') {
        console.log('Project and brand are already public, opening in new tab');
        //setShowDownloadTooltip(false);
        
        // If already public, open in new tab with brand_id
        //window.open(`/brands/${project.id}?brand_id=${currentBrand.id}&preview=true`, '_blank');
        return;
      }

      console.log('Making project and brand public');
      // Update both project and brand to public
      const updatedBrands = [...project.brands];
      updatedBrands[selectedBrandIndex] = {
        ...currentBrand,
        access: 'public'
      };

      // Update project in Firestore
      const updatedProject = await updateProject(project.id, {
        ...project,
        access: 'public',
        brands: updatedBrands
      });

      console.log('Project updated:', updatedProject);
      // Update local state
      setProject(updatedProject);

      // Open in new tab with brand_id
      // window.open(`/brands/${project.id}?brand_id=${currentBrand.id}&preview=true`, '_blank');
      
      // Check if user has been asked for survey
      const user = auth.currentUser;
      const userId = user?.uid;
      if (!(await hasBeenAskedForSurvey(userId))) {
        setShowSurveyPopup(true);
        await markSurveyAsked(userId);
      }
      
      // Track brand sharing
      trackBrandShared(currentBrand.id);
      
    } catch (error) {
      console.error('Error sharing project:', error);
      showToast('Failed to share project. Please try again.');
      trackError('brand_share_error', error.message);
    }

    //setShowDownloadTooltip(false);
  };

  const shouldHideUIElements = () => {
    const isPreview = new URLSearchParams(window.location.search).get('preview') === 'true';
    const isNonAuthUser = !auth.currentUser;
    const isNotOwner = auth.currentUser && project && project.userId !== auth.currentUser.uid;
    const isPublic = project?.brands?.[0]?.access === 'public';

    console.log("isPreview", isPreview);
    console.log("isNonAuthUser", isNonAuthUser);
    console.log("isNotOwner", isNotOwner);
    console.log("isPublic", isPublic);

    return (isPreview || isNonAuthUser || isNotOwner) && isPublic;
  };

  const handleCopyLink = async () => {
    const currentBrand = project.brands[selectedBrandIndex];
    const shareUrl = `${window.location.origin}/brands/${project.id}?brand_id=${currentBrand.id}&preview=true`;
    await navigator.clipboard.writeText(shareUrl);
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 2000);
  };

  useEffect(() => {
    // Track page view when component mounts
    trackPageView('brand-generator');
  }, []);

  // Add useEffect for handling pre-fill
  useEffect(() => {
    // Only run for slide 5 (index 4)
    const slideIndex = 4;
    const inputIndex = 1; // The text input index
    const currentRadioSelection = radioSelections[`${slideIndex}-0`];
    const currentTextValue = promptValues[`${slideIndex}-${inputIndex}`];
    
    if (currentRadioSelection) {
      let prefilledValue = '';
      switch (currentRadioSelection) {
        case "We're not the loudest — our product speaks.":
          prefilledValue = "We're not the loudest — our product speaks.";
          break;
        case "We don't pitch. We show.":
          prefilledValue = "We don't pitch. We show.";
          break;
        case "What makes us different is…":
          prefilledValue = "What makes us different is…";
          break;
        case "We're the ones who…":
          prefilledValue = "We're the ones who…";
          break;
        case "I separate myself by…":
          prefilledValue = "I separate myself by…";
          break;
        case "Write my own":
          prefilledValue = "";
          break;
      }
      
      // Only update if:
      // 1. We're loading an existing project and the radio selection matches the text value
      // 2. We're filling out the form for the first time
      if (currentTextValue === prefilledValue || !currentTextValue) {
        handleInputChange(slideIndex, inputIndex, prefilledValue);
      }
    }
  }, [radioSelections['4-0']]); // Only run when radio selection changes for slide 5

  return (
    <div className="generate-container">
      {/* Remove the first overlay instance */}
      <div className="portfolio-page">
        <section className="portfolio-crev valign pt-80">
          <div className="container-fluid rest">
            <div 
              className="work-boxs" 
              ref={workBoxRef}
              style={{
                transition: 'all 0.8s ease-in-out',
                position: 'relative'
              }}
            >
              {data.map((item, i) => {
                const isActive = i === activeIndex;
                const isFinalActive = isLastSubmitted && i === data.length - 1;
                const shouldRender = isActive || isFinalActive || showContent;
                
                if (!shouldRender) return null;
                
                return (
                  <div
                    onClick={() => isMobile ? null : handleOpenWork(i)}
                    key={i}
                    className={`item ${isActive || isFinalActive ? 'active' : ''}`}
                    style={{
                      ...getItemWidth(i, isLastSubmitted, isActive),
                      ...(isFinalActive ? {
                        width: '100%',
                        height: '90vh',
                        position: 'relative',
                        zIndex: 10
                      } : {})
                    }}
                  >
                    <div className="bg-img" style={{ 
                      position: isMobile ? 'fixed' : undefined,
                      top: isMobile ? 50 : undefined,
                      left: isMobile ? 0 : undefined,
                      overflow: 'hidden',
                      height: '100%',
                      transition: 'all 1.5s cubic-bezier(0.645, 0.045, 0.355, 1)'
                    }}>
                      {/* Video as background - use final video if in final state */}
                      <video
                        ref={el => videoRefs.current[i] = el}
                        src={isFinalActive && finalVideo ? finalVideo : item.img}
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          zIndex: 0,
                          filter: isActive || isFinalActive ? 'brightness(1)' : 'brightness(0.6)',
                          transition: 'filter 0.8s ease'
                        }}
                      />
                      
                      {/* Subtitle positioned at top center - hide only for final video background */}
                      {!isFinalActive && (
                        <div style={{
                          position: 'absolute',
                          top: '20px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          {/* Steps indicator */}
                          <div style={{
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            padding: '8px 16px',
                            borderRadius: '20px',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: '500',
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            transition: 'all 0.3s ease'
                          }}>
                            {item.subTitle}
                          </div>
                        </div>
                      )}
                      
                      {/* Navigation Arrows */}
                      {isActive && !isLastSubmitted && (
                        <>
                          {/* Forward Arrow - Show on all slides except last */}
                          {i < data.length - 1 && (
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleNextSlide(e);
                              }}
                              style={{
                                position: 'absolute',
                                top: '20px',
                                right: '20px',
                                zIndex: 2,
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                padding: '8px',
                                borderRadius: '50%',
                                color: '#fff',
                                cursor: 'pointer',
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                transition: 'all 0.3s ease',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px'
                              }}
                            >
                              <svg 
                                width="20" 
                                height="20" 
                                viewBox="0 0 24 24" 
                                fill="none" 
                                stroke="currentColor" 
                                strokeWidth="2" 
                                strokeLinecap="round" 
                                strokeLinejoin="round"
                              >
                                <path d="M5 12h14M12 5l7 7-7 7"/>
                              </svg>
                            </div>
                          )}

                          {/* Navigation Button - Back button when no project, Menu button when project exists */}
                          {(i > 0 || project) && (
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (project) {
                                  // If project exists, show menu
                                  setShowOverlay(!showOverlay);
                                } else {
                                  // If no project, go back
                                  setActiveIndex(i - 1);
                                }
                              }}
                              style={{
                                position: 'absolute',
                                top: '20px',
                                left: '20px',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                padding: '8px',
                                borderRadius: '50%',
                                backdropFilter: 'blur(4px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '36px',
                                height: '36px',
                                zIndex: 3
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                              }}
                            >
                              {project ? (
                                <svg 
                                  width="20" 
                                  height="20" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <line x1="3" y1="12" x2="21" y2="12"></line>
                                  <line x1="3" y1="6" x2="21" y2="6"></line>
                                  <line x1="3" y1="18" x2="21" y2="18"></line>
                                </svg>
                              ) : (
                                <svg 
                                  width="20" 
                                  height="20" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                                </svg>
                              )}
                            </div>
                          )}

                          {/* Tooltip Menu - Only show when project exists and overlay is active */}
                          {project && showOverlay && (
                            <div style={{
                              position: 'absolute',
                              top: '70px',
                              left: '20px',
                              backgroundColor: 'rgba(0, 0, 0, 0.9)',
                              borderRadius: '10px',
                              padding: '10px',
                              minWidth: '200px',
                              zIndex: 9999,
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(10px)',
                              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                              animation: 'fadeIn 0.2s ease-out'
                            }}>
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '5px'
                              }}>
                                {data.map((item, index) => (
                                  <button
                                    key={index}
                                    onClick={() => {
                                      if (item.title === "Brand Blueprint") {
                                        // If it's Brand Blueprint, show the final example slide
                                        setShowExampleSlide(true);
                                        setHelloTextState('hidden');
                                        setShowWhiteDiv(false);
                                        setIsAnimatingOut(true);
                                        setRightSideExpanded(true);
                                      } else {
                                        // For other items, use the normal redirection
                                        handleRedirection(index);
                                      }
                                      setShowOverlay(false);
                                    }}
                                    style={{
                                      padding: '10px 15px',
                                      backgroundColor: activeIndex === index ? 'rgba(253, 91, 56, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                      border: '1px solid rgba(255, 255, 255, 0.1)',
                                      borderRadius: '8px',
                                      color: activeIndex === index ? '#fd5b38' : '#fff',
                                      textAlign: 'left',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '10px',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = activeIndex === index ? 'rgba(253, 91, 56, 0.1)' : 'rgba(255, 255, 255, 0.05)';
                                    }}
                                  >
                                    <span style={{
                                      color: '#fd5b38',
                                      fontSize: '14px',
                                      fontWeight: 'bold',
                                      minWidth: '20px'
                                    }}>{index + 1}</span>
                                    <span style={{
                                      fontSize: '14px'
                                    }}>{item.title}</span>
                                  </button>
                                ))}
                                <button
                                  onClick={() => {
                                    console.log('Brand Blueprint button clicked');
                                    // If it's Brand Blueprint, show the final example slide
                                    setShowExampleSlide(true);
                                    setHelloTextState('hidden');
                                    setShowWhiteDiv(false);
                                    setIsAnimatingOut(true);
                                    setRightSideExpanded(true);

                                    setShowOverlay(false);
                                  }}
                                  style={{
                                    padding: '10px 15px',
                                    backgroundColor: showExampleSlide ? 'rgba(253, 91, 56, 0.1)' : 'rgba(255, 255, 255, 0.05)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    borderRadius: '8px',
                                    color: showExampleSlide ? '#fd5b38' : '#fff',
                                    textAlign: 'left',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    marginTop: '5px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = showExampleSlide ? 'rgba(253, 91, 56, 0.1)' : 'rgba(255, 255, 255, 0.05)';
                                  }}
                                >
                                  <span style={{
                                    color: '#fd5b38',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    minWidth: '20px'
                                  }}>{data.length + 1}</span>
                                  <span style={{
                                    fontSize: '14px'
                                  }}>Brand Blueprint</span>
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Only render content div if not in final state or if this isn't the active final item */}
                      {(!isLastSubmitted || !isFinalActive) && (
                        <div 
                          className="cont main-bg" 
                          style={{ 
                            position: isMobile ? 'fixed' : undefined,
                            top: isMobile ? 100 : undefined,
                            left: isMobile ? 0 : undefined,
                            borderRadius: '15px',
                            width: 'calc(100% - 20px)',
                            margin: '10px',
                            padding: '20px',
                            boxShadow: '0 5px 15px rgba(0,0,0,0.2)',
                            transition: 'all 0.8s ease',
                            height: isFinalActive ? 'auto' : undefined,
                            maxHeight: isMobile ? 'calc(100vh - 120px)' : undefined,
                            overflowY: isMobile ? 'auto' : 'visible',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: isFinalActive ? 'center' : 'flex-start',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            backdropFilter: 'blur(10px)',
                            zIndex: isMobile && !isFinalActive ? 10 : isMobile ? 9 : 3
                          }}
                        >
                          <div style={{
                            opacity: showContent ? 1 : 0,
                            transform: showContent ? 'translateY(0)' : 'translateY(20px)',
                            transition: showContent ? 'opacity 0.5s ease, transform 0.5s ease' : 'none',
                            width: '100%',
                            visibility: showContent ? 'visible' : 'hidden'
                          }}>
                            <div className="d-flex align-items-center">
                              <div>
                                <span style={{
                                  fontSize: '2rem',
                                  fontWeight: '600',
                                  lineHeight: '1.2',
                                  marginBottom: '0.75rem',
                                  display: 'block',
                                  color: 'white',
                                }} className="p-color">
                                  {isFinalActive ? 'Final Result' : item.title}
                                </span>
                                <span className="p-color mt-5">
                                  {isFinalActive ? 'Your prompts have been combined' : item.question}
                                </span>
                              </div>
                              <div className="ml-auto">
                                 
                              </div>
                            </div>
                            
                            {/* Input field - only visible in active slide and not in final state */}
                            {isActive && !isLastSubmitted && (
                              <form 
                                onSubmit={(e) => {
                                  e.preventDefault(); // Prevent default form submission
                                  if (project) {
                                    e.preventDefault();
                                    // Show the final example slide
                                    setShowExampleSlide(true);
                                    setHelloTextState('hidden');
                                    setShowWhiteDiv(false);
                                    setIsAnimatingOut(true);
                                    setRightSideExpanded(true);
                                    setShowOverlay(false);
                                  } else if (i === data.length - 1) {
                                    // If on last slide but no project, create new project with animation
                                    setGenerateClicked(true);
                                    setFinalVideo('/assets/video/vid16.mp4');
                                    setIsLastSubmitted(true);
                                    
                                    // Show the example slide after a delay
                                    setTimeout(() => {
                                      if(isMobile){
                                        // do nothing
                                      }else{
                                        setShowExampleSlide(true);
                                        setHelloTextState('hidden');
                                        setShowWhiteDiv(false);
                                        setIsAnimatingOut(true);
                                        setRightSideExpanded(true);
                                      }
                                    }, 1500);
                                    
                                    // Then proceed with project creation
                                    handleSubmit(e, i);
                                  } else {
                                    // For all other slides, proceed normally
                                    handleNextSlide(e);
                                  }
                                }} 
                                style={{ 
                                  marginTop: '20px',
                                  borderTop: '1px solid rgba(255,255,255,0.1)',
                                  paddingTop: '20px',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: '20px'
                                }}
                              >
                                {renderInput(item, i, () => handleInputModification(item.title))}
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  width: '100%',
                                  gap: '20px'
                                }}>
                                  <button
                                    type="submit"
                                    style={{
                                      padding: '12px 40px',
                                      borderRadius: '10px',
                                      border: 'none',
                                      background: '#fd5b38',
                                      color: '#fff',
                                      fontSize: '14px',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      minWidth: '50%',
                                      maxWidth: '400px'
                                    }}
                                  >
                                    {i === 0 ? "Let's Go" : (i === data.length - 1 ? (project ? 'Finished Editing' : 'Generate') : 'Submit')}
                                  </button>
                                  {i === 3 && (
                                    <span
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleNextSlide(e);
                                      }}
                                      style={{
                                        color: '#fd5b38',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.opacity = '0.7';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.opacity = '1';
                                      }}
                                    >
                                      Skip for now
                                    </span>
                                  )}
                                </div>
                              </form>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {showExampleSlide && (
                <div
                  className="item example-slide active"
                  style={{
                    position: isMobile ? 'fixed' : 'relative',
                    top: isMobile ? 50 : undefined,
                    left: isMobile ? 0 : undefined,
                    width: startOnExampleSlide ? '100%' : 0,
                    height: '90vh',
                    margin: 0,
                    opacity: 1,
                    transition: 'all 1.5s cubic-bezier(0.645, 0.045, 0.355, 1)',
                    zIndex: 20,
                    visibility: 'visible'
                  }}
                >
                  <div className="bg-img" style={{ 
                    position: 'relative', 
                    overflow: 'hidden',
                    height: '100%',
                    backgroundColor: '#111',
                    transition: 'all 1.5s cubic-bezier(0.645, 0.045, 0.355, 1)',
                    borderRadius: '15px',
                    display: 'flex'
                  }}>
                    {/* Left side with Hello text - Only show if not authenticated */}
                    {!isAnimatingOut && (
                      <div style={{
                        width: auth.currentUser || localStorage.getItem('isAuthenticated') === 'true' ? '0' : '60%',
                        display: auth.currentUser || localStorage.getItem('isAuthenticated') === 'true' ? 'none' : 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        overflow: 'hidden',
                        gap: '20px',
                        transition: 'all 2s ease',
                        opacity: 1,
                        position: 'relative',
                        zIndex: 2
                      }}>
                        <h1 
                          ref={helloTextRef}
                          style={{
                            color: '#fff',
                            fontSize: '6rem',
                            fontWeight: 'bold',
                            opacity: helloTextState === 'visible' ? 1 : 0,
                            transform: helloTextState === 'visible' ? 'translateY(0)' : 'translateY(30px)',
                            transition: 'all 2s ease',
                            pointerEvents: 'none'
                          }}
                        >
                          Your Brand's Blueprint
                        </h1>
                        
                        {/* White div that appears after text animation */}
                        <div
                          ref={whiteDivRef}
                          style={{
                            width: '440px',
                            height: 'auto',
                            backgroundColor: '#121212',
                            opacity: showWhiteDiv ? 1 : 0,
                            transform: showWhiteDiv ? 'translateY(0)' : 'translateY(30px)',
                            transition: 'all 2s ease',
                            borderRadius: '15px',
                            pointerEvents: 'auto',
                            padding: '30px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                            zIndex: 30,
                            border: '1px solid rgba(255,255,255,0.1)'
                          }}
                        >
                          {/* Authentication form content */}
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '20px',
                            width: '100%'
                          }}>
                            {!verificationSent ? (
                              <>
                                <input
                                  type="email"
                                  placeholder="Enter your email address"
                                  value={email}
                                  onChange={(e) => setEmail(e.target.value)}
                                  required
                                  style={{
                                    padding: '12px 15px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border-color 0.3s ease',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    backgroundColor: '#0f0f0f',
                                    color: '#fff'
                                  }}
                                />
                                <input
                                  type="password"
                                  placeholder="Enter strong password"
                                  value={password}
                                  onChange={(e) => setPassword(e.target.value)}
                                  required
                                  minLength="8"
                                  style={{
                                    padding: '12px 15px',
                                    borderRadius: '8px',
                                    border: '1px solid rgba(255,255,255,0.1)',
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'border-color 0.3s ease',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    backgroundColor: '#0f0f0f',
                                    color: '#fff'
                                  }}
                                />
                                
                                <button
                                  onClick={handleEmailSignUp}
                                  disabled={isEmailLoading || isGoogleLoading}
                                  style={{
                                    padding: '12px 15px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    backgroundColor: '#fd5b38',
                                    color: '#fff',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: isEmailLoading || isGoogleLoading ? 'not-allowed' : 'pointer',
                                    width: '100%',
                                    transition: 'all 0.3s ease',
                                    marginBottom: '10px',
                                    opacity: isEmailLoading || isGoogleLoading ? 0.7 : 1
                                  }}
                                >
                                  {isEmailLoading ? 'Signing up...' : 'Sign Up'}
                                </button>
                              </>
                            ) : (
                              <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                backgroundColor: '#0f0f0f',
                                borderRadius: '8px',
                                marginBottom: '20px',
                                border: '1px solid rgba(255,255,255,0.1)'
                              }}>
                                <h3 style={{ marginBottom: '10px', color: '#fff' }}>Verification Email Sent!</h3>
                                <p style={{ color: '#c6c8c9', fontSize: '14px', lineHeight: '1.5' }}>
                                  We've sent a verification email to <strong>{email}</strong>.<br />
                                  Please check your inbox and click the verification link to complete your registration.
                                  {isVerified && <span><br />Your email has been verified! You can now continue.</span>}
                                </p>
                              </div>
                            )}
                            
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              margin: '10px 0'
                            }}>
                              <div style={{
                                flex: 1,
                                height: '1px',
                                backgroundColor: 'rgba(255,255,255,0.1)'
                              }} />
                              <span style={{
                                color: '#c6c8c9',
                                fontSize: '14px'
                              }}>OR</span>
                              <div style={{
                                flex: 1,
                                height: '1px',
                                backgroundColor: 'rgba(255,255,255,0.1)'
                              }} />
                            </div>
                            
                            <button
                              onClick={handleGoogleSignIn}
                              disabled={isEmailLoading || isGoogleLoading}
                              style={{
                                padding: '12px 15px',
                                borderRadius: '8px',
                                border: '1px solid rgba(255,255,255,0.1)',
                                backgroundColor: '#0f0f0f',
                                color: '#fff',
                                fontSize: '14px',
                                cursor: isEmailLoading || isGoogleLoading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '10px',
                                transition: 'all 0.3s ease',
                                width: '100%',
                                opacity: isEmailLoading || isGoogleLoading ? 0.7 : 1
                              }}
                            >
                              <img 
                                src={googleLogo}
                                alt="Google"
                                style={{
                                  width: '20px',
                                  height: '20px'
                                }}
                              />
                              {isGoogleLoading ? 'Signing in...' : 'Sign in with Google'}
                            </button>
                            
                            {error && (
                              <div style={{
                                color: '#fd5b38',
                                fontSize: '12px',
                                marginTop: '10px',
                                textAlign: 'center'
                              }}>
                                {error}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Right side with glass effect */}
                    <div style={{
                      width: rightSideExpanded ? '100%' : '40%',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      boxSizing: 'border-box',
                      padding: '20px 20px 30px 20px',
                      transition: 'all 2s ease',
                      transform: rightSideExpanded ? 'translateX(0)' : 'translateX(0)',
                      transformOrigin: 'right',
                      marginLeft: 'auto'
                    }}>
                      {/* Background image with padding */}
                      <div className="bg-img" style={{
                        position: 'absolute',
                        top: 20,
                        left: 20,
                        right: 20,
                        bottom: 30,
                        backgroundImage: true ? `url(${'/assets/imgs/background.jpg'})`: `url(${preloadedImageUrl || '/assets/imgs/checkout.png'})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: '15px',
                        zIndex: 0,
                        transition: 'background-image 0.5s ease',
                        width: 'calc(100% - 40px)',
                        height: 'calc(100% - 50px)'
                      }}
                      onError={async (e) => {
                        console.error('Background image failed to load:', e);
                        if (preloadedImageUrl) {
                          // we don't want a background image anymore
                          return;
                          const freshUrl = await getFreshSignedUrl(preloadedImageUrl);
                          if (freshUrl) {
                            e.target.style.backgroundImage = `url(${freshUrl})`;
                          }
                        }
                      }}></div>
                      
                      {/* Glass effect overlay - show during brand switching or initial load */}
                      {(true || isBrandSwitch || (!isImageLoaded && !project?.brands?.length)) && (
                        <div style={{
                          position: 'absolute',
                          top: 20,
                          left: 20,
                          right: 20,
                          bottom: 30,
                          background: 'rgba(40, 40, 40, 0.3)',
                          backdropFilter: 'blur(12px)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '15px',
                          zIndex: 1,
                          overflow: 'hidden',
                          transition: 'opacity 0.3s ease'
                        }}>
                          {/* Dynamic color bands - doubled for seamless looping */}
                          <div style={{
                            position: 'absolute',
                            top: '-10%',
                            left: `-${animationOffset}%`,
                            width: '200%',
                            height: '120%',
                            backgroundImage: `
                              linear-gradient(90deg, 
                                rgba(255, 255, 255, 0) 0%,
                                rgba(255, 255, 255, 0.05) 10%,
                                rgba(255, 120, 50, 0.1) 12.5%, 
                                rgba(70, 120, 255, 0.15) 17.5%, 
                                rgba(255, 255, 255, 0.05) 25%,
                                rgba(255, 255, 255, 0) 30%,
                                rgba(255, 255, 255, 0.05) 35%,
                                rgba(70, 200, 255, 0.1) 40%,
                                rgba(255, 120, 50, 0.15) 45%,
                                rgba(255, 255, 255, 0) 50%,
                                rgba(255, 255, 255, 0) 50%,
                                rgba(255, 255, 255, 0.05) 60%,
                                rgba(255, 120, 50, 0.1) 62.5%, 
                                rgba(70, 120, 255, 0.15) 67.5%, 
                                rgba(255, 255, 255, 0.05) 75%,
                                rgba(255, 255, 255, 0) 80%,
                                rgba(255, 255, 255, 0.05) 85%,
                                rgba(70, 200, 255, 0.1) 90%,
                                rgba(255, 120, 50, 0.15) 95%,
                                rgba(255, 255, 255, 0) 100%
                              )
                            `,
                            backgroundSize: '100% 100%',
                            pointerEvents: 'none',
                            transform: 'skewX(-20deg)'
                          }}/>
                          
                          {/* Shine overlay */}
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `
                              radial-gradient(
                                circle at ${50 + Math.sin(animationOffset/5) * 30}% ${50 + Math.cos(animationOffset/3) * 20}%, 
                                rgba(255, 255, 255, 0.2) 0%, 
                                rgba(255, 255, 255, 0) 40%
                              )
                            `,
                            pointerEvents: 'none',
                            animation: 'shine 2s ease-in-out infinite'
                          }}/>
                          <style>
                            {`
                              @keyframes shine {
                                0% { opacity: 0.3; }
                                50% { opacity: 0.8; }
                                100% { opacity: 0.3; }
                              }
                            `}
                          </style>
                        </div>
                      )}
                      
                      {/* Content inside glass effect */}
                      <div className="right-side-content" style={{
                        position: 'relative',
                        zIndex: 2,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                      }}>
                        

                        {project?.brands?.[0]?.imageUrl && isImageLoaded ? (
                          <>
                            {/* Dropdown Menu */}
                            <div style={{
                              position: 'absolute',
                              top: '20px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              //zIndex: 3
                            }}>
                              <select
                                style={{
                                  padding: '8px 16px',
                                  borderRadius: '20px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  color: '#fff',
                                  fontSize: '14px',
                                  cursor: 'pointer',
                                  backdropFilter: 'blur(4px)',
                                  outline: 'none',
                                  appearance: 'none',
                                  paddingRight: !shouldHideUIElements() ? '30px' : '0',
                                  marginRight: !shouldHideUIElements() ? '0' : '16px',
                                  minWidth: !shouldHideUIElements() ? (isMobile ? '200px' : '') : isMobile ? "0" : "100%",
                                  maxWidth: !shouldHideUIElements() ? (isMobile ? '100px' : '') : isMobile ? "0" : "100%",
                                  textAlign: 'center',
                                  textAlignLast: 'center',
                                  display: shouldHideUIElements() && isMobile ? 'none' : 'undefined',
                                }}
                                value={selectedBrandIndex}
                                onFocus={() => isMobile && setIsSelectOpen(true)}
                                onBlur={() => isMobile && setIsSelectOpen(false)}
                                onChange={async (e) => {
                                  const selectedIndex = parseInt(e.target.value);
                                  if (isMobile && selectedIndex === -1) return; // Don't do anything if 'Menu' is selected
                                  const selectedBrand = project.brands[selectedIndex];
                                  console.log('BRAND SELECTION - Dropdown changed:', {
                                    selectedIndex,
                                    selectedBrand,
                                    imageUrl: selectedBrand?.imageUrl,
                                    allBrands: project.brands,
                                    currentPreloadedUrl: preloadedImageUrl,
                                    currentSelectedIndex: selectedBrandIndex
                                  });

                                  if (selectedBrand) {
                                    // Update selected brand index
                                    setSelectedBrandIndex(selectedIndex);
                                    // Set brand switching state to true
                                    setIsBrandSwitch(true);
                                    
                                    // Get a fresh signed URL for the selected brand's image
                                    const freshUrl = await getFreshSignedUrl(selectedBrand.imageUrl);
                                    console.log('Fresh signed URL:', {
                                      original: selectedBrand.imageUrl,
                                      fresh: freshUrl
                                    });

                                    // Create a new image object to handle loading
                                    const img = new Image();
                                    img.onload = () => {
                                      console.log('New brand image loaded successfully');
                                      // Update the image URL and set loaded state
                                      setPreloadedImageUrl(freshUrl || selectedBrand.imageUrl);
                                      setIsImageLoaded(true);
                                      // Reset brand switching state after image is loaded
                                      setIsBrandSwitch(false);
                                    };
                                    img.onerror = async () => {
                                      console.error('Failed to load brand image:', selectedBrand.imageUrl);
                                      // Try one more time with the original URL if fresh URL fails
                                      const img2 = new Image();
                                      img2.onload = () => {
                                        setPreloadedImageUrl(selectedBrand.imageUrl);
                                        setIsImageLoaded(true);
                                        // Reset brand switching state after image is loaded
                                        setIsBrandSwitch(false);
                                      };
                                      img2.onerror = () => {
                                        console.error('Failed to load brand image even with original URL');
                                        // Reset brand switching state on error
                                        setIsBrandSwitch(false);
                                      };
                                      img2.src = selectedBrand.imageUrl;
                                    };
                                    img.src = freshUrl || selectedBrand.imageUrl;

                                    // Update the project state with the selected brand
                                    setProject(prevProject => {
                                      console.log('Updating project state:', {
                                        previousBrand: prevProject.currentBrand,
                                        newBrand: selectedBrand
                                      });
                                      return {
                                        ...prevProject,
                                        currentBrandIndex: selectedIndex,
                                        currentBrand: selectedBrand
                                      };
                                    });
                                  } else {
                                    console.warn('Selected brand not found:', {
                                      selectedIndex,
                                      availableBrands: project.brands
                                    });
                                  }
                                }}
                                disabled={shouldHideUIElements()}
                              >
                                {isMobile && !isSelectOpen && (
                                  <option value={-1} disabled style={{ display: 'none' }}>Menu</option>
                                )}
                                {project.brands.map((brand, index) => (
                                  <option key={index} value={index}>
                                    {brand.tagline || `Brand Style ${index + 1}`}
                                  </option>
                                ))}
                              </select>
                              {!shouldHideUIElements() && (
                                <div style={{
                                  position: 'absolute',
                                  right: '12px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  pointerEvents: 'none',
                                  color: '#fff'
                                }}>
                                  <svg 
                                    width="12" 
                                    height="12" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                  </svg>
                                </div>
                              )}
                            </div>

                            {/* Mobile Tagline Display */}
                            {shouldHideUIElements() && isMobile && (
                              <div style={{
                                position: 'fixed',
                                top: '20px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: '80%',
                                padding: '8px 16px',
                                borderRadius: '20px',
                                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                backdropFilter: 'blur(4px)',
                                display: (expandedSection && expandedSection !== 'positioning') ? 'none' : 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1000,
                                marginTop: '20px'
                              }}>
                                <span style={{
                                  color: '#fff',
                                  fontSize: '14px',
                                  textAlign: 'center',
                                  whiteSpace: 'normal',
                                  wordWrap: 'break-word'
                                }}>
                                  {project?.brands?.[selectedBrandIndex]?.tagline || `Brand Style ${selectedBrandIndex + 1}`}
                                </span>
                              </div>
                            )}

                            {!shouldHideUIElements() && (
                              <>
                                {/* Menu button with tooltip */}
                                <div style={{ 
                                  position: 'absolute',
                                  top: '20px',
                                  left: '20px',
                                  zIndex: 3
                                }}>
                                  <button
                                    onClick={() => {
                                      console.log('Menu button clicked:', {
                                        currentShowOverlay: showOverlay,
                                        activeIndex,
                                        activeSection
                                      });
                                      setShowOverlay(!showOverlay);
                                      console.log('Overlay state toggled to:', !showOverlay);
                                    }}
                                    style={{
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      border: '1px solid rgba(255, 255, 255, 0.1)',
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                                    }}
                                  >
                                    <svg 
                                      width="20" 
                                      height="20" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <line x1="3" y1="12" x2="21" y2="12"></line>
                                      <line x1="3" y1="6" x2="21" y2="6"></line>
                                      <line x1="3" y1="18" x2="21" y2="18"></line>
                                    </svg>
                                  </button>

                                  {/* Tooltip Menu */}
                                  {showOverlay && (
                                    <div style={{
                                      position: 'absolute',
                                      top: '50px',
                                      left: '0',
                                      backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                      borderRadius: '10px',
                                      padding: '10px',
                                      minWidth: '200px',
                                      zIndex: 1000,
                                      border: '1px solid rgba(255, 255, 255, 0.1)',
                                      backdropFilter: 'blur(10px)',
                                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                      animation: 'fadeIn 0.2s ease-out'
                                    }}>
                                      <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '5px'
                                      }}>
                                        {data.map((item, index) => (
                                          <button
                                            key={index}
                                            onClick={() => {
                                              if (item.title === "Brand Blueprint") {
                                                // If it's Brand Blueprint, show the final example slide
                                                setShowExampleSlide(true);
                                                setHelloTextState('hidden');
                                                setShowWhiteDiv(false);
                                                setIsAnimatingOut(true);
                                                setRightSideExpanded(true);
                                              } else {
                                                // For other items, use the normal redirection
                                                handleRedirection(index);
                                              }
                                              setShowOverlay(false);
                                            }}
                                            style={{
                                              padding: '10px 15px',
                                              backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                              border: '1px solid rgba(255, 255, 255, 0.1)',
                                              borderRadius: '8px',
                                              color: '#fff',
                                              textAlign: 'left',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '10px',
                                              cursor: 'pointer',
                                              transition: 'all 0.3s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                            }}
                                          >
                                            <span style={{
                                              color: '#fd5b38',
                                              fontSize: '14px',
                                              fontWeight: 'bold',
                                              minWidth: '20px'
                                            }}>{index + 1}</span>
                                            <span style={{
                                              fontSize: '14px'
                                            }}>{item.title}</span>
                                          </button>
                                        ))}
                                        <button
                                          onClick={() => {
                                            console.log('Brand Blueprint button clicked');
                                            // If it's Brand Blueprint, show the final example slide
                                            setShowExampleSlide(true);
                                            setHelloTextState('hidden');
                                            setShowWhiteDiv(false);
                                            setIsAnimatingOut(true);
                                            setRightSideExpanded(true);

                                            setShowOverlay(false);
                                          }}
                                          style={{
                                            padding: '10px 15px',
                                            backgroundColor: 'rgba(253, 91, 56, 0.1)',
                                            border: '1px solid rgba(253, 91, 56, 0.2)',
                                            borderRadius: '8px',
                                            color: '#fd5b38',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            marginTop: '5px'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(253, 91, 56, 0.15)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(253, 91, 56, 0.1)';
                                          }}
                                        >
                                          <span style={{
                                            color: '#fd5b38',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            minWidth: '20px'
                                          }}>{data.length + 1}</span>
                                          <span style={{
                                            fontSize: '14px'
                                          }}>Brand Blueprint</span>
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <style>
                                  {`
                                    @keyframes fadeIn {
                                      from {
                                        opacity: 0;
                                        transform: translateY(-10px);
                                      }
                                      to {
                                        opacity: 1;
                                        transform: translateY(0);
                                      }
                                    }
                                  `}
                                </style>

                                {/* Retry button */}
                                <button
                                  onClick={() => handleRetryGeneration(false)}
                                  style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '20px',
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: retryError ? 'rgba(253, 91, 56, 0.2)' : 'rgba(0, 0, 0, 0.5)',
                                    border: `1px solid ${retryError ? 'rgba(253, 91, 56, 0.5)' : 'rgba(255, 255, 255, 0.1)'}`,
                                    color: retryError ? '#fd5b38' : '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    zIndex: 3,
                                    animation: retryErrorAnimation ? 'bounce 0.5s ease-in-out' : 'none'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = retryError ? 'rgba(253, 91, 56, 0.3)' : 'rgba(0, 0, 0, 0.7)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = retryError ? 'rgba(253, 91, 56, 0.2)' : 'rgba(0, 0, 0, 0.5)';
                                  }}
                                >
                                  <svg 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  >
                                    <path d="M23 4v6h-6M1 20v-6h6"/>
                                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                  </svg>
                                </button>
                                {retryError && (
                                  <div style={{
                                    position: 'absolute',
                                    top: '70px',
                                    right: '20px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fd5b38',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 4,
                                    animation: retryErrorAnimation ? 'bounce 0.5s ease-in-out' : 'none',
                                    border: '1px solid rgba(253, 91, 56, 0.3)'
                                  }}>
                                    {retryError}
                                  </div>
                                )}
                                <style>
                                  {`
                                    @keyframes bounce {
                                      0%, 100% { transform: translateY(0); }
                                      50% { transform: translateY(-5px); }
                                    }
                                  `}
                                </style>

                                {/* Download button */}
                                <button
                                  onClick={() => setShowDownloadTooltip(!showDownloadTooltip)}
                                  disabled={isDownloadLoading}
                                  style={{
                                    position: 'absolute',
                                    bottom: '20px',
                                    right: '20px',
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: isDownloadLoading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    zIndex: 3
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                                  }}
                                >
                                  {isDownloadLoading ? (
                                    <div style={{ width: 20, height: 20, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                                  ) : (
                                    <svg 
                                      width="20" 
                                      height="20" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                                      <polyline points="16 6 12 2 8 6"/>
                                      <line x1="12" y1="2" x2="12" y2="15"/>
                                    </svg>
                                  )}
                                </button>

                                {/* Download tooltip */}
                                {showDownloadTooltip && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '70px',
                                    right: '20px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fff',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 4,
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    minWidth: '200px',
                                    animation: 'fadeIn 0.2s ease-out'
                                  }}>
                                    <button
                                      onClick={async () => {
                                        setIsDownloadLoading(true);
                                        const pdf = await generatePDF();
                                        setIsDownloadLoading(false);
                                        setShowDownloadTooltip(false);

                                        const user = auth.currentUser;
                                        const userId = user?.uid;
                                        if (!(await hasBeenAskedForSurvey(userId))) {
                                          setShowSurveyPopup(true);
                                          await markSurveyAsked(userId);
                                        }
                                      }}
                                      style={{
                                        padding: '10px 15px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '8px',
                                        color: '#fff',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                      }}
                                    >
                                      <svg 
                                        width="16" 
                                        height="16" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2" 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round"
                                      >
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                        <polyline points="7 10 12 15 17 10"/>
                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                      </svg>
                                      Download PDF
                                    </button>

                                    {project?.brands?.[selectedBrandIndex]?.access === 'public' ? (
                                      <>
                                        <button
                                          onClick={async () => {
                                            try {
                                              // Get the current brand
                                              const currentBrand = project.brands[selectedBrandIndex];
                                              
                                              // Update brand to private
                                              const updatedBrands = [...project.brands];
                                              updatedBrands[selectedBrandIndex] = {
                                                ...currentBrand,
                                                access: 'private'
                                              };

                                              // Check if any brands are still public
                                              const hasPublicBrands = updatedBrands.some(brand => brand.access === 'public');
                                              
                                              // Update project in Firestore
                                              const updatedProject = await updateProject(project.id, {
                                                ...project,
                                                access: hasPublicBrands ? 'public' : 'private',
                                                brands: updatedBrands
                                              });

                                              // Update local state
                                              setProject(updatedProject);
                                              setShowDownloadTooltip(false);
                                            } catch (error) {
                                              console.error('Error making brand private:', error);
                                              showToast('Failed to make brand private. Please try again.');
                                            }
                                          }}
                                          style={{
                                            padding: '10px 15px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                            border: '1px solid rgba(255, 255, 255, 0.1)',
                                            borderRadius: '8px',
                                            color: '#fff',
                                            textAlign: 'left',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                          }}
                                        >
                                          <svg 
                                            width="16" 
                                            height="16" 
                                            viewBox="0 0 24 24" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            strokeWidth="2" 
                                            strokeLinecap="round" 
                                            strokeLinejoin="round"
                                          >
                                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                                            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                                          </svg>
                                          Make Private
                                        </button>

                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          width: '100%',
                                          gap: '8px',
                                          padding: '10px 15px',
                                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                          border: '1px solid rgba(255, 255, 255, 0.1)',
                                          borderRadius: '8px',
                                          opacity: isGeneratingLink ? 0 : 1,
                                          transform: `translateY(${isGeneratingLink ? '10px' : '0'})`,
                                          transition: 'all 0.3s ease'
                                        }}>
                                          <input
                                            type="text"
                                            value={`${window.location.origin}/brands/${project.id}?brand_id=${project.brands[selectedBrandIndex].id}&preview=true`}
                                            readOnly
                                            style={{
                                              flex: 1,
                                              background: 'rgba(0, 0, 0, 0.2)',
                                              border: '1px solid rgba(255, 255, 255, 0.1)',
                                              borderRadius: '6px',
                                              padding: '8px 12px',
                                              color: '#fff',
                                              fontSize: '14px',
                                              cursor: 'default'
                                            }}
                                          />
                                          <button
                                            onClick={handleCopyLink}
                                            style={{
                                              background: isCopied ? '#4CAF50' : 'rgba(255, 255, 255, 0.1)',
                                              border: '1px solid rgba(255, 255, 255, 0.1)',
                                              borderRadius: '6px',
                                              padding: '8px',
                                              color: '#fff',
                                              cursor: 'pointer',
                                              transition: 'all 0.3s ease',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center'
                                            }}
                                          >
                                            {isCopied ? (
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M20 6L9 17l-5-5"/>
                                              </svg>
                                            ) : (
                                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                                                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                                              </svg>
                                            )}
                                          </button>
                                        </div>
                                      </>
                                    ) : (
                                      <button
                                        onClick={async () => {
                                          setIsGeneratingLink(true);
                                          await onShare();
                                          setTimeout(() => {
                                            setIsGeneratingLink(false);
                                          }, 300);
                                        }}
                                        style={{
                                          padding: '10px 15px',
                                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                          border: '1px solid rgba(255, 255, 255, 0.1)',
                                          borderRadius: '8px',
                                          color: '#fff',
                                          textAlign: 'left',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px',
                                          cursor: 'pointer',
                                          transition: 'all 0.3s ease',
                                          opacity: isGeneratingLink ? 0 : 1,
                                          transform: `translateY(${isGeneratingLink ? '-10px' : '0'})`
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                                        }}
                                      >
                                        <svg 
                                          width="16" 
                                          height="16" 
                                          viewBox="0 0 24 24" 
                                          fill="none" 
                                          stroke="currentColor" 
                                          strokeWidth="2" 
                                          strokeLinecap="round" 
                                          strokeLinejoin="round"
                                        >
                                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                        </svg>
                                        Generate Link
                                      </button>
                                    )}
                                  </div>
                                )}

                                {/* Delete button */}
                                <button
                                  onClick={() => {
                                    console.log('DELETE BUTTON - Clicked:', {
                                      currentBrandIndex: selectedBrandIndex,
                                      currentBrand: project.brands[selectedBrandIndex],
                                      allBrands: project.brands,
                                      currentPreloadedUrl: preloadedImageUrl
                                    });
                                    setShowDeleteConfirm(true);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    bottom: '20px',
                                    left: '20px',
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    zIndex: 3
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                                  }}
                                >
                                  <svg 
                                    width="20" 
                                    height="20" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                  >
                                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  </svg>
                                </button>

                                {/* Delete confirmation tooltip */}
                                {showDeleteConfirm && (
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '70px',
                                    left: '20px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fff',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    whiteSpace: 'nowrap',
                                    zIndex: 4,
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '12px',
                                    minWidth: '200px',
                                    animation: project.brands.length === 1 ? 'bounce 0.5s ease-in-out' : 'none'
                                  }}>
                                    <div style={{
                                      color: project.brands.length === 1 ? '#fd5b38' : '#fff'
                                    }}>
                                      {project.brands.length === 1 
                                        ? 'Deleting the last brand will delete this project'
                                        : 'Are you sure you want to delete?'}
                                    </div>
                                    <div style={{
                                      display: 'flex',
                                      gap: '8px',
                                      justifyContent: 'flex-end'
                                    }}>
                                      <button
                                        onClick={() => setShowDeleteConfirm(false)}
                                        style={{
                                          padding: '6px 12px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(255, 255, 255, 0.1)',
                                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                          color: '#fff',
                                          fontSize: '14px',
                                          cursor: 'pointer',
                                          transition: 'all 0.3s ease'
                                        }}
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        onClick={() => {
                                          deleteFromBrand(selectedBrandIndex);
                                          setShowDeleteConfirm(false);
                                        }}
                                        style={{
                                          padding: '6px 12px',
                                          borderRadius: '4px',
                                          border: '1px solid rgba(253, 91, 56, 0.3)',
                                          backgroundColor: 'rgba(253, 91, 56, 0.1)',
                                          color: '#fd5b38',
                                          fontSize: '14px',
                                          cursor: 'pointer',
                                          transition: 'all 0.3s ease'
                                        }}
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}

                            {/* Add the overlay here, inside the right-side-content */}
                            {showOverlay && activeSection && (
                              <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 1000,
                                animation: 'fadeInLeft 0.3s ease-out forwards'
                              }}>
                                <div style={{
                                  width: '80%',
                                  maxWidth: '800px',
                                  backgroundColor: 'rgba(0, 0, 0, 0.9)',
                                  borderRadius: '15px',
                                  padding: '30px',
                                  position: 'relative',
                                  border: '1px solid rgba(255, 255, 255, 0.1)',
                                  maxHeight: '90vh',
                                  overflowY: 'auto',
                                  opacity: 0,
                                  transform: 'translateX(-20px)',
                                  animation: 'fadeInLeft 0.3s ease-out forwards'
                                }}>
                                  {/* Close button */}
                                  <button
                                    onClick={handleCloseOverlay}
                                    style={{
                                      position: 'absolute',
                                      top: '20px',
                                      right: '20px',
                                      width: '40px',
                                      height: '40px',
                                      borderRadius: '50%',
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      border: '1px solid rgba(255, 255, 255, 0.1)',
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer',
                                      transition: 'all 0.3s ease',
                                      zIndex: 3
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
                                    }}
                                  >
                                    <svg 
                                      width="20" 
                                      height="20" 
                                      viewBox="0 0 24 24" 
                                      fill="none" 
                                      stroke="currentColor" 
                                      strokeWidth="2" 
                                      strokeLinecap="round" 
                                      strokeLinejoin="round"
                                    >
                                      <line x1="18" y1="6" x2="6" y2="18"></line>
                                      <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* Update the animation keyframes */}
                            <style>
                              {`
                                @keyframes fadeInLeft {
                                  from {
                                    opacity: 0;
                                    transform: translateX(-20px);
                                  }
                                  to {
                                    opacity: 1;
                                    transform: translateX(0);
                                  }
                                }
                              `}
                            </style>

                            {/* Add the Generate button at the bottom center */}
                            {!shouldHideUIElements() && Object.values(modifiedInputs).some(value => value) && (
                              <button
                                onClick={async () => {
                                  try {
                                    // Create updated project data with new form values
                                    const updatedProjectData = {
                                      title: promptValues[0] || project.title,
                                      description: promptValues[1] || project.description,
                                      images: uploadedImages.map(img => ({
                                        url: img.gcsUrl,
                                        previewUrl: img.url,
                                        uploadedAt: new Date().toISOString()
                                      })),
                                      settings: {
                                        checkboxSelections: Object.entries(checkboxSelections).reduce((acc, [slideIndex, selections]) => ({
                                          ...acc,
                                          [slideIndex]: selections
                                        }), {}),
                                        radioSelections: Object.entries(radioSelections).reduce((acc, [slideIndex, selection]) => ({
                                          ...acc,
                                          [slideIndex]: selection
                                        }), {}),
                                        multiSelectRadioSelections: multiSelectRadioSelections,
                                        sliderValues: Object.entries(sliderValues).reduce((acc, [slideIndex, values]) => ({
                                          ...acc,
                                          [slideIndex]: values
                                        }), {}),
                                        multipleTextValues: Object.entries(multipleTextValues).reduce((acc, [slideIndex, values]) => ({
                                          ...acc,
                                          [slideIndex]: values
                                        }), {}),
                                        promptValues: Object.entries(promptValues).map(([key, value]) => {
                                          const [slideIndex, inputIndex] = key.split('-');
                                          return {
                                            slideIndex: parseInt(slideIndex),
                                            inputIndex: parseInt(inputIndex),
                                            value: value
                                          };
                                        })
                                      }
                                    };

                                    // Update project in Firestore
                                    const updatedProject = await updateProject(project.id, updatedProjectData);
                                    
                                    // Update local project state
                                    setProject(updatedProject);

                                    // Reset all modified states
                                    setModifiedInputs({
                                      Story: false,
                                      Talk: false,
                                      BBQ: false,
                                      Look: false,
                                      Game: false,
                                      Image: false
                                    });

                                    // Now trigger the retry generation
                                    handleRetryGeneration(false);
                                  } catch (error) {
                                    console.error('Error updating project:', error);
                                    showToast('Failed to save changes. Please try again.');
                                  }
                                }}
                                style={{
                                  position: 'absolute',
                                  bottom: '20px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  padding: '12px 40px',
                                  borderRadius: '20px',
                                  backgroundColor: '#fd5b38',
                                  border: '1px solid rgba(253, 91, 56, 0.5)',
                                  color: '#fff',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.3s ease',
                                  backdropFilter: 'blur(4px)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  zIndex: 3,
                                  boxShadow: '0 4px 12px rgba(253, 91, 56, 0.2)'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#ff6b4a';
                                  e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fd5b38';
                                  e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
                                }}
                              >
                                <svg 
                                  width="16" 
                                  height="16" 
                                  viewBox="0 0 24 24" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  strokeWidth="2" 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round"
                                >
                                  <path d="M23 4v6h-6M1 20v-6h6"/>
                                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                                </svg>
                                Generate
                              </button>
                            )}

                            {/* Positioning Statement Section */}
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: '10px',
                              padding: '20px',
                              marginTop: '20px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(4px)',
                              maxHeight: expandedSection === 'positioning' ? '50%' : 'auto',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                                <h3 style={{
                                  color: '#fff',
                                  fontSize: '18px',
                                  marginBottom: '15px',
                                  fontWeight: '500',
                                  marginTop: '0'
                                }}>
                                  Positioning Statement
                                </h3>
                                <button
                                  onClick={() => setExpandedSection(expandedSection === 'positioning' ? null : 'positioning')}
                                  style={{
                                    background: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '50%',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '-10px',
                                    transition: 'all 0.3s ease',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <svg 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    style={{
                                      transform: expandedSection === 'positioning' ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s ease'
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                  </svg>
                                </button>
                              </div>
                              <div style={{
                                color: '#c6c8c9',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                overflowY: expandedSection === 'positioning' ? 'auto' : 'hidden',
                                flex: 1,
                                paddingRight: '10px',
                                maxHeight: expandedSection === 'positioning' ? '500px' : '0',
                                opacity: expandedSection === 'positioning' ? 1 : 0,
                                transition: 'all 0.3s ease'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formatMarkdownText(project?.brands?.[selectedBrandIndex]?.positioningStatement || 'No positioning statement data available')
                              }}
                              />
                            </div>

                            {/* Brand Manifesto Section */}
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: '10px',
                              padding: '20px',
                              marginTop: '20px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(4px)',
                              maxHeight: expandedSection === 'manifesto' ? '50%' : 'auto',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                                <h3 style={{
                                  color: '#fff',
                                  fontSize: '18px',
                                  marginBottom: '15px',
                                  fontWeight: '500',
                                  marginTop: '0'
                                }}>
                                  Brand Manifesto
                                </h3>
                                <button
                                  onClick={() => setExpandedSection(expandedSection === 'manifesto' ? null : 'manifesto')}
                                  style={{
                                    background: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '50%',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '-10px',
                                    transition: 'all 0.3s ease',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <svg 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    style={{
                                      transform: expandedSection === 'positioning' ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s ease'
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                  </svg>
                                </button>
                              </div>
                              <div style={{
                                color: '#c6c8c9',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                overflowY: expandedSection === 'manifesto' ? 'auto' : 'hidden',
                                flex: 1,
                                paddingRight: '10px',
                                maxHeight: expandedSection === 'manifesto' ? '500px' : '0',
                                opacity: expandedSection === 'manifesto' ? 1 : 0,
                                transition: 'all 0.3s ease'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formatMarkdownText(project?.brands?.[selectedBrandIndex]?.brandManifesto || 'No brand manifesto data available')
                              }}
                              />
                            </div>

                            {/* Audience Matrix Section */}
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: '10px',
                              padding: '20px',
                              marginTop: '20px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(4px)',
                              maxHeight: expandedSection === 'audience' ? '50%' : 'auto',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                                <h3 style={{
                                  color: '#fff',
                                  fontSize: '18px',
                                  marginBottom: '15px',
                                  fontWeight: '500',
                                  marginTop: '0'
                                }}>
                                  Audience Matrix
                                </h3>
                                <button
                                  onClick={() => setExpandedSection(expandedSection === 'audience' ? null : 'audience')}
                                  style={{
                                    background: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '50%',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '-10px',
                                    transition: 'all 0.3s ease',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <svg 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    style={{
                                      transform: expandedSection === 'audience' ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s ease'
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                  </svg>
                                </button>
                              </div>
                              <div style={{
                                color: '#c6c8c9',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                overflowY: expandedSection === 'audience' ? 'auto' : 'hidden',
                                flex: 1,
                                paddingRight: '10px',
                                maxHeight: expandedSection === 'audience' ? '500px' : '0',
                                opacity: expandedSection === 'audience' ? 1 : 0,
                                transition: 'all 0.3s ease'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formatMarkdownText(project?.brands?.[selectedBrandIndex]?.audienceMatrix || 'No audience matrix data available')
                              }}
                              />
                            </div>

                            {/* Visual Vibe Summary Section */}
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: '10px',
                              padding: '20px',
                              marginTop: '20px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(4px)',
                              maxHeight: expandedSection === 'visualVibe' ? '50%' : 'auto',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                                <h3 style={{
                                  color: '#fff',
                                  fontSize: '18px',
                                  marginBottom: '15px',
                                  fontWeight: '500',
                                  marginTop: '0'
                                }}>
                                  Visual Vibe Summary
                                </h3>
                                <button
                                  onClick={() => setExpandedSection(expandedSection === 'visualVibe' ? null : 'visualVibe')}
                                  style={{
                                    background: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '50%',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '-10px',
                                    transition: 'all 0.3s ease',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <svg 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    style={{
                                      transform: expandedSection === 'visualVibe' ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s ease'
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                  </svg>
                                </button>
                              </div>
                              <div style={{
                                color: '#c6c8c9',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                overflowY: expandedSection === 'visualVibe' ? 'auto' : 'hidden',
                                flex: 1,
                                paddingRight: '10px',
                                maxHeight: expandedSection === 'visualVibe' ? '500px' : '0',
                                opacity: expandedSection === 'visualVibe' ? 1 : 0,
                                transition: 'all 0.3s ease'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formatMarkdownText(project?.brands?.[selectedBrandIndex]?.visualVibeSummary || 'No visual vibe summary available')
                              }}
                              />
                            </div>

                            {/* Tone & Mood Profile Section */}
                            <div style={{
                              backgroundColor: 'rgba(0, 0, 0, 0.5)',
                              borderRadius: '10px',
                              padding: '20px',
                              marginTop: '20px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              backdropFilter: 'blur(4px)',
                              maxHeight: expandedSection === 'tonemood' ? '50%' : 'auto',
                              width: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              transition: 'all 0.3s ease'
                            }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%'
                              }}>
                                <h3 style={{
                                  color: '#fff',
                                  fontSize: '18px',
                                  marginBottom: '15px',
                                  fontWeight: '500',
                                  marginTop: '0'
                                }}>
                                  Tone & Mood Profile
                                </h3>
                                <button
                                  onClick={() => setExpandedSection(expandedSection === 'tonemood' ? null : 'tonemood')}
                                  style={{
                                    background: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '50%',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    padding: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginTop: '-10px',
                                    transition: 'all 0.3s ease',
                                    width: '32px',
                                    height: '32px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.4)';
                                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }}
                                >
                                  <svg 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                    style={{
                                      transform: expandedSection === 'tonemood' ? 'rotate(180deg)' : 'rotate(0deg)',
                                      transition: 'transform 0.3s ease'
                                    }}
                                  >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                  </svg>
                                </button>
                              </div>
                              <div style={{
                                color: '#c6c8c9',
                                fontSize: '14px',
                                lineHeight: '1.6',
                                whiteSpace: 'pre-wrap',
                                overflowY: expandedSection === 'tonemood' ? 'auto' : 'hidden',
                                flex: 1,
                                paddingRight: '10px',
                                maxHeight: expandedSection === 'tonemood' ? '500px' : '0',
                                opacity: expandedSection === 'tonemood' ? 1 : 0,
                                transition: 'all 0.3s ease'
                              }}
                              dangerouslySetInnerHTML={{
                                __html: formatMarkdownText(project?.brands?.[selectedBrandIndex]?.toneMoodProfile || 'No tone & mood profile available')
                              }}
                              />
                            </div>
                            
                          </>
                        ) : (
                          <div style={{
                            textAlign: 'center',
                            color: '#fff',
                            fontSize: '1.2rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '20px'
                          }}>
                            <div style={{
                              width: '50px',
                              height: '50px',
                              border: '3px solid rgba(255,255,255,0.1)',
                              borderTop: '3px solid #fd5b38',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            {!isBrandSwitch && !project?.brands?.length && (
                              <div className="pulsating-text" style={{
                                color: 'white',
                                fontSize: '1.2rem',
                                animation: 'pulse 2s infinite',
                                textAlign: 'center'
                              }}>
                                {messages[loadingMessage]}
                              </div>
                            )}
                            <style>
                              {`
                                @keyframes spin {
                                  0% { transform: rotate(0deg); }
                                  100% { transform: rotate(360deg); }
                                }
                                @keyframes pulse {
                                  0% { opacity: 0.6; transform: scale(0.98); }
                                  50% { opacity: 1; transform: scale(1); }
                                  100% { opacity: 0.6; transform: scale(0.98); }
                                }
                              `}
                            </style>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
      {/* AUTH POPUP OVERLAY */}
      {authPopUp && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            width: '440px',
            backgroundColor: '#121212',
            borderRadius: '15px',
            padding: '30px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.1)',
            zIndex: 10000,
            position: 'relative',
          }}>
            {/* Authentication form content (copied from lines 2911–3112) */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              width: '100%'
            }}>
              {!verificationSent ? (
                <>
                  <input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      padding: '12px 15px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: '#0f0f0f',
                      color: '#fff'
                    }}
                  />
                  <input
                    type="password"
                    placeholder="Enter strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength="8"
                    style={{
                      padding: '12px 15px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255,255,255,0.1)',
                      fontSize: '14px',
                      outline: 'none',
                      transition: 'border-color 0.3s ease',
                      width: '100%',
                      boxSizing: 'border-box',
                      backgroundColor: '#0f0f0f',
                      color: '#fff'
                    }}
                  />
                  <button
                    onClick={handleEmailSignUp}
                    disabled={isEmailLoading || isGoogleLoading}
                    style={{
                      padding: '12px 15px',
                      borderRadius: '8px',
                      border: 'none',
                      backgroundColor: '#fd5b38',
                      color: '#fff',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: isEmailLoading || isGoogleLoading ? 'not-allowed' : 'pointer',
                      width: '100%',
                      transition: 'all 0.3s ease',
                      marginBottom: '10px',
                      opacity: isEmailLoading || isGoogleLoading ? 0.7 : 1
                    }}
                  >
                    {isEmailLoading ? 'Signing up...' : 'Sign Up'}
                  </button>
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  backgroundColor: '#0f0f0f',
                  borderRadius: '8px',
                  marginBottom: '20px',
                  border: '1px solid rgba(255,255,255,0.1)'
                }}>
                  <h3 style={{ marginBottom: '10px', color: '#fff' }}>Verification Email Sent!</h3>
                  <p style={{ color: '#c6c8c9', fontSize: '14px', lineHeight: '1.5' }}>
                    We've sent a verification email to <strong>{email}</strong>.<br />
                    Please check your inbox and click the verification link to complete your registration.
                    {isVerified && <span><br />Your email has been verified! You can now continue.</span>}
                  </p>
                </div>
              )}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                margin: '10px 0'
              }}>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }} />
                <span style={{
                  color: '#c6c8c9',
                  fontSize: '14px'
                }}>OR</span>
                <div style={{
                  flex: 1,
                  height: '1px',
                  backgroundColor: 'rgba(255,255,255,0.1)'
                }} />
              </div>
              <button
                onClick={handleGoogleSignIn}
                disabled={isEmailLoading || isGoogleLoading}
                style={{
                  padding: '12px 15px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  backgroundColor: '#0f0f0f',
                  color: '#fff',
                  fontSize: '14px',
                  cursor: isEmailLoading || isGoogleLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'all 0.3s ease',
                  width: '100%',
                  opacity: isEmailLoading || isGoogleLoading ? 0.7 : 1
                }}
              >
                <img 
                  src={googleLogo}
                  alt="Google"
                  style={{
                    width: '20px',
                    height: '20px'
                  }}
                />
                {isGoogleLoading ? 'Signing in...' : 'Sign in with Google'}
              </button>
              {error && (
                <div style={{
                  color: '#fd5b38',
                  fontSize: '12px',
                  marginTop: '10px',
                  textAlign: 'center'
                }}>
                  {error}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {showSurveyPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999
        }}>
          <div style={{
            background: 'rgba(20,20,20,0.98)',
            borderRadius: '16px',
            padding: '32px 24px',
            minWidth: 320,
            maxWidth: '90vw',
            boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
            color: '#fff',
            textAlign: 'center',
            border: '1px solid rgba(255,255,255,0.08)'
          }}>
            <div style={{fontSize: 22, fontWeight: 600, marginBottom: 12}}>Download successful!</div>
            <div style={{fontSize: 16, marginBottom: 24}}>
              I hope you're enjoying NarrativeOS™.<br />
              Help us fill out a quick survey.
            </div>
            <div style={{display: 'flex', gap: 16, justifyContent: 'center'}}>
              <button
                onClick={() => setShowSurveyPopup(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'rgba(255,255,255,0.12)',
                  color: '#fff',
                  fontWeight: 500,
                  cursor: 'pointer',
                  fontSize: 16
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowSurveyPopup(false);
                  window.open('/survey', '_blank');
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: 6,
                  border: 'none',
                  background: 'linear-gradient(90deg, #fd5b38 0%, #4678ff 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: 16
                }}
              >
                Take Survey
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Portfolio;
